/* AllySabers "Ignition" homepage behaviour: scroll reveals, header state, hero slideshow,
   core lab and product rails. Plain JS, no dependencies. Works with the Shopify theme editor. */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------- */
  /* Reveal on scroll                                            */
  /* ---------------------------------------------------------- */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-inview');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 })
    : null;

  const observeReveals = (scope = document) => {
    scope.querySelectorAll('[data-ig-reveal]:not(.is-inview)').forEach(el => {
      // Anything already on screen is shown immediately so nothing flickers.
      const rect = el.getBoundingClientRect();
      if (!revealObserver || reduceMotion.matches || rect.top < window.innerHeight * 0.92) {
        el.classList.add('is-inview');
      } else {
        revealObserver.observe(el);
      }
    });
  };

  observeReveals();
  root.classList.add('ig-js');

  /* ---------------------------------------------------------- */
  /* Header: solid once the page has scrolled                    */
  /* ---------------------------------------------------------- */
  const onScroll = () => root.classList.toggle('ig-scrolled', window.scrollY > 24);
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });

  /* ---------------------------------------------------------- */
  /* Hero slideshow                                              */
  /* ---------------------------------------------------------- */
  class IgHero extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.ig-hero__slide')];
      this.tabs = [...this.querySelectorAll('.ig-hero__tab')];
      this.duration = (parseFloat(this.dataset.duration) || 7) * 1000;
      this.index = Math.max(0, this.slides.findIndex(s => s.classList.contains('is-active')));
      this.style.setProperty('--ig-duration', `${this.duration / 1000}s`);
      if (this.slides.length < 2) return;

      this.static = reduceMotion.matches || this.dataset.autoplay === 'false';
      this.classList.toggle('is-static', this.static);

      this.tabs.forEach((tab, i) => tab.addEventListener('click', () => this.go(i, true)));
      // Pause only while the pointer is on the text, buttons or tabs. Pausing on the whole hero (which fills the
      // screen) left the progress blade frozen whenever the mouse happened to rest on the page.
      // Real mouse only: on phones a tap fires a fake "mouseenter" with no "mouseleave", which used to freeze the
      // progress blade (and autoplay) until the visitor tapped somewhere else.
      this.hoverZones = [...this.querySelectorAll('.ig-hero__copy, .ig-hero__nav')];
      this.mouseOver = null;
      this.hoverZones.forEach(zone => {
        zone.addEventListener('pointerenter', e => {
          if (e.pointerType !== 'mouse') return;
          this.mouseOver = zone;
          this.pause();
        });
        zone.addEventListener('pointerleave', e => {
          if (e.pointerType !== 'mouse') return;
          if (this.mouseOver === zone) this.mouseOver = null;
          this.resume();
        });
      });
      // Keyboard focus pauses (so people can read), but a mouse click on a tab must not freeze the slideshow.
      this.addEventListener('focusin', e => { if (e.target.matches(':focus-visible')) this.pause(); });
      this.addEventListener('focusout', () => setTimeout(() => this.resume(), 0));
      document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.resume()));

      // Swipe on touch screens
      let startX = null;
      this.addEventListener('pointerdown', e => { if (e.pointerType !== 'mouse') startX = e.clientX; });
      this.addEventListener('pointerup', e => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        startX = null;
        if (Math.abs(dx) > 50) this.go(this.index + (dx < 0 ? 1 : -1), true);
      });
      // A touch never leaves the slideshow paused
      this.addEventListener('touchend', () => { if (this.paused && !this.mouseOver) this.resume(); }, { passive: true });

      this.go(this.index, false);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
    }

    go(i, user) {
      const n = this.slides.length;
      this.index = (i + n) % n;
      this.slides.forEach((s, k) => {
        const active = k === this.index;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', active ? 'false' : 'true');
        s.inert = !active;
      });
      this.tabs.forEach((t, k) => {
        t.setAttribute('aria-current', k === this.index ? 'true' : 'false');
        // restart the progress blade
        const fill = t.querySelector('.ig-hero__fill');
        if (fill) { fill.style.animation = 'none'; void fill.offsetWidth; fill.style.animation = ''; }
      });
      const blade = this.slides[this.index].style.getPropertyValue('--blade');
      if (blade) this.style.setProperty('--blade', blade);
      this.started = Date.now();
      this.remaining = this.duration;
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.static || this.paused) return;
      this.timer = setTimeout(() => this.go(this.index + 1), this.remaining);
    }

    pause() {
      if (this.static || this.paused) return;
      this.paused = true;
      this.classList.add('is-paused');
      clearTimeout(this.timer);
      this.remaining = Math.max(0, this.remaining - (Date.now() - this.started));
    }

    resume() {
      const hovered = this.mouseOver && this.mouseOver.matches(':hover');
      if (this.static || !this.paused || hovered || (this.contains(document.activeElement) && document.activeElement.matches(':focus-visible'))) return;
      this.paused = false;
      this.classList.remove('is-paused');
      this.started = Date.now();
      this.timer = setTimeout(() => this.go(this.index + 1), this.remaining || this.duration);
    }
  }

  /* ---------------------------------------------------------- */
  /* Saber sound: synthesised with Web Audio (no audio files).   */
  /* Off by default; only starts after the visitor turns it on.  */
  /* ---------------------------------------------------------- */
  class SaberSound {
    ensure() {
      if (this.ctx) return true;
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return false;
      const ctx = (this.ctx = new AC());
      this.out = ctx.createGain();
      this.out.gain.value = 0.32;
      this.out.connect(ctx.destination);

      // Hum: two slightly detuned saws + a sub, through a resonant low-pass
      this.filter = ctx.createBiquadFilter();
      this.filter.type = 'lowpass';
      this.filter.frequency.value = 360;
      this.filter.Q.value = 3;
      this.hum = ctx.createGain();
      this.hum.gain.value = 0;
      this.filter.connect(this.hum).connect(this.out);
      this.oscs = [[90, 'sawtooth', 0.5], [91.3, 'sawtooth', 0.5], [45, 'triangle', 0.8]].map(([f, type, g]) => {
        const o = ctx.createOscillator();
        const v = ctx.createGain();
        o.type = type;
        o.frequency.value = f;
        v.gain.value = g;
        o.connect(v).connect(this.filter);
        o.start();
        return o;
      });
      // Slow wobble on the filter so the hum feels alive
      const lfo = ctx.createOscillator();
      const depth = ctx.createGain();
      lfo.frequency.value = 0.7;
      depth.gain.value = 45;
      lfo.connect(depth).connect(this.filter.frequency);
      lfo.start();

      // Noise for the ignition crackle
      const len = ctx.sampleRate;
      this.noise = ctx.createBuffer(1, len, ctx.sampleRate);
      const data = this.noise.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
      return true;
    }

    resume() {
      if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume();
    }

    sweep(from, to, dur, peak) {
      const ctx = this.ctx;
      const t = ctx.currentTime;
      const o = ctx.createOscillator();
      const bp = ctx.createBiquadFilter();
      const g = ctx.createGain();
      o.type = 'sawtooth';
      o.frequency.setValueAtTime(from, t);
      o.frequency.exponentialRampToValueAtTime(to, t + dur);
      bp.type = 'bandpass';
      bp.frequency.setValueAtTime(from * 4, t);
      bp.frequency.exponentialRampToValueAtTime(to * 4, t + dur);
      bp.Q.value = 1.2;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(peak, t + dur * 0.25);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.connect(bp).connect(g).connect(this.out);
      o.start(t);
      o.stop(t + dur + 0.05);
    }

    crackle(dur) {
      const ctx = this.ctx;
      const t = ctx.currentTime;
      const src = ctx.createBufferSource();
      const bp = ctx.createBiquadFilter();
      const g = ctx.createGain();
      src.buffer = this.noise;
      bp.type = 'bandpass';
      bp.frequency.value = 1400;
      bp.Q.value = 0.8;
      g.gain.setValueAtTime(0.18, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      src.connect(bp).connect(g).connect(this.out);
      src.start(t);
      src.stop(t + dur);
    }

    ignite(core) {
      if (!this.ensure()) return;
      this.resume();
      const t = this.ctx.currentTime;
      const dur = core === 'base' ? 0.32 : core === 'proffie' ? 0.8 : 0.62;
      this.sweep(70, 340, dur * 0.55, 0.28);
      this.crackle(core === 'base' ? 0.18 : 0.3);
      this.hum.gain.cancelScheduledValues(t);
      this.hum.gain.setValueAtTime(this.hum.gain.value, t);
      this.hum.gain.linearRampToValueAtTime(0.2, t + dur * 0.5);
      this.hum.gain.setTargetAtTime(0.13, t + dur, 0.25);
      this.lit = true;
    }

    retract() {
      if (!this.ctx) return;
      const t = this.ctx.currentTime;
      this.sweep(320, 55, 0.45, 0.22);
      this.hum.gain.cancelScheduledValues(t);
      this.hum.gain.setValueAtTime(this.hum.gain.value, t);
      this.hum.gain.linearRampToValueAtTime(0, t + 0.42);
      this.lit = false;
    }

    // speed: 0..1 from pointer movement
    swing(speed) {
      if (!this.ctx || !this.lit) return;
      const t = this.ctx.currentTime;
      const f = this.filter.frequency;
      const g = this.hum.gain;
      f.cancelScheduledValues(t);
      g.cancelScheduledValues(t);
      f.setTargetAtTime(360 + 1300 * speed, t, 0.03);
      g.setTargetAtTime(0.13 + 0.17 * speed, t, 0.03);
      f.setTargetAtTime(360, t + 0.12, 0.18);
      g.setTargetAtTime(0.13, t + 0.12, 0.18);
      this.oscs.forEach((o, i) => {
        const base = [90, 91.3, 45][i];
        o.frequency.cancelScheduledValues(t);
        o.frequency.setTargetAtTime(base * (1 + 0.12 * speed), t, 0.04);
        o.frequency.setTargetAtTime(base, t + 0.12, 0.2);
      });
    }

    silence() {
      if (!this.ctx) return;
      const t = this.ctx.currentTime;
      this.hum.gain.cancelScheduledValues(t);
      this.hum.gain.setTargetAtTime(0, t, 0.06);
      clearTimeout(this.sleep);
      this.sleep = setTimeout(() => this.ctx.state === 'running' && this.ctx.suspend(), 400);
    }

    wake() {
      if (!this.ctx) return;
      clearTimeout(this.sleep);
      this.resume();
      if (this.lit) this.hum.gain.setTargetAtTime(0.13, this.ctx.currentTime, 0.2);
    }

    /* ---- Effect sounds used by the blade lab ---- */

    burst(freq, q, peak, dur, type = 'bandpass') {
      const ctx = this.ctx;
      const t = ctx.currentTime;
      const src = ctx.createBufferSource();
      const f = ctx.createBiquadFilter();
      const g = ctx.createGain();
      src.buffer = this.noise;
      src.playbackRate.value = 0.8 + Math.random() * 0.4;
      f.type = type;
      f.frequency.value = freq;
      f.Q.value = q;
      g.gain.setValueAtTime(peak, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      src.connect(f).connect(g).connect(this.out);
      src.start(t, Math.random() * 0.5);
      src.stop(t + dur + 0.02);
    }

    tone(from, to, dur, peak, type = 'sine') {
      const ctx = this.ctx;
      const t = ctx.currentTime;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type;
      o.frequency.setValueAtTime(from, t);
      o.frequency.exponentialRampToValueAtTime(to, t + dur);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(peak, t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.connect(g).connect(this.out);
      o.start(t);
      o.stop(t + dur + 0.02);
    }

    clash() {
      if (!this.ctx || !this.lit) return;
      this.resume();
      this.burst(2400, 0.7, 0.55, 0.22);
      this.burst(700, 1.1, 0.35, 0.3);
      this.tone(140, 45, 0.22, 0.4);
    }

    blaster() {
      if (!this.ctx || !this.lit) return;
      this.resume();
      this.tone(1900, 240, 0.2, 0.2, 'triangle');
      this.burst(3200, 0.9, 0.3, 0.09);
    }

    // Sustained effects (lockup, tip drag): looping noise with a fast tremolo
    loop(kind, on) {
      if (!this.ctx) return;
      this.loops = this.loops || {};
      const t = this.ctx.currentTime;
      if (on && !this.loops[kind] && this.lit) {
        this.resume();
        const ctx = this.ctx;
        const src = ctx.createBufferSource();
        const f = ctx.createBiquadFilter();
        const g = ctx.createGain();
        const am = ctx.createGain();
        const lfo = ctx.createOscillator();
        const lfoDepth = ctx.createGain();
        src.buffer = this.noise;
        src.loop = true;
        f.type = kind === 'drag' ? 'highpass' : 'bandpass';
        f.frequency.value = kind === 'drag' ? 2600 : 1300;
        f.Q.value = kind === 'drag' ? 0.6 : 1.4;
        lfo.frequency.value = kind === 'drag' ? 31 : 19;
        lfoDepth.gain.value = 0.5;
        am.gain.value = 0.5;
        lfo.connect(lfoDepth).connect(am.gain);
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(kind === 'drag' ? 0.22 : 0.34, t + 0.05);
        src.connect(f).connect(am).connect(g).connect(this.out);
        src.start(t);
        lfo.start(t);
        this.loops[kind] = { src, lfo, g };
        if (kind === 'lockup') this.clash();
      } else if (!on && this.loops[kind]) {
        const l = this.loops[kind];
        l.g.gain.cancelScheduledValues(t);
        l.g.gain.setTargetAtTime(0.0001, t, 0.04);
        l.src.stop(t + 0.25);
        l.lfo.stop(t + 0.25);
        delete this.loops[kind];
      }
    }

    // Unstable and fire styles add a crackle under the hum
    texture(level) {
      if (!this.ensure()) return;
      const ctx = this.ctx;
      if (!this.crackleBus) {
        const src = ctx.createBufferSource();
        const f = ctx.createBiquadFilter();
        const am = ctx.createGain();
        const lfo = ctx.createOscillator();
        const depth = ctx.createGain();
        this.crackleBus = ctx.createGain();
        this.crackleBus.gain.value = 0;
        src.buffer = this.noise;
        src.loop = true;
        f.type = 'bandpass';
        f.frequency.value = 900;
        f.Q.value = 0.9;
        lfo.type = 'square';
        lfo.frequency.value = 13;
        depth.gain.value = 0.5;
        am.gain.value = 0.5;
        lfo.connect(depth).connect(am.gain);
        src.connect(f).connect(am).connect(this.crackleBus).connect(this.out);
        src.start();
        lfo.start();
      }
      this.crackleBus.gain.setTargetAtTime(this.lit ? level : 0, ctx.currentTime, 0.08);
    }
  }

  /* ---------------------------------------------------------- */
  /* Core lab                                                    */
  /* ---------------------------------------------------------- */
  class IgCoreLab extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('.ig-lab__stage');
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.power = this.querySelector('.ig-power:not([data-sound])');
      this.swatches = [...this.querySelectorAll('.ig-swatch')];
      this.coreLabel = this.querySelector('[data-core-label]');
      this.colorLabel = this.querySelector('[data-color-label]');
      this.caption = this.querySelector('[data-lab-caption]');

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', e => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.tabs[next].focus();
          this.select(next);
        });
      });

      this.swatches.forEach(sw => sw.addEventListener('click', () => {
        this.swatches.forEach(s => s.setAttribute('aria-pressed', s === sw ? 'true' : 'false'));
        this.style.setProperty('--blade', sw.dataset.color);
        if (this.colorLabel) this.colorLabel.textContent = sw.dataset.name;
      }));

      this.power?.addEventListener('click', () => (this.stage.classList.contains('is-off') ? this.ignite() : this.retract()));

      // Optional sound
      this.soundBtn = this.querySelector('[data-sound]');
      if (this.soundBtn && (window.AudioContext || window.webkitAudioContext)) {
        this.soundBtn.addEventListener('click', () => this.toggleSound());
        this.stage.addEventListener('pointermove', e => this.onSwing(e));
        this.visible = true;
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(entries => {
            this.visible = entries[0].isIntersecting;
            if (!this.sound) return;
            this.visible ? this.sound.wake() : this.sound.silence();
          }).observe(this);
        }
        document.addEventListener('visibilitychange', () => {
          if (!this.sound) return;
          document.hidden ? this.sound.silence() : this.visible && this.sound.wake();
        });
      } else if (this.soundBtn) {
        this.soundBtn.hidden = true;
      }

      // Ignite the first time the lab scrolls into view.
      this.stage.classList.add('is-off');
      if ('IntersectionObserver' in window && !reduceMotion.matches) {
        const io = (this.io = new IntersectionObserver(entries => {
          if (entries.some(e => e.isIntersecting)) { io.disconnect(); setTimeout(() => this.ignite(), 250); }
        }, { threshold: 0.45 }));
        io.observe(this.stage);
      } else {
        this.ignite();
      }
    }

    select(i) {
      this.tabs.forEach((t, k) => {
        t.setAttribute('aria-selected', k === i ? 'true' : 'false');
        t.tabIndex = k === i ? 0 : -1;
      });
      this.panels.forEach((p, k) => (p.hidden = k !== i));
      const tab = this.tabs[i];
      this.stage.dataset.core = tab.dataset.core;
      if (this.coreLabel) this.coreLabel.textContent = tab.dataset.name;
      if (this.caption) this.caption.textContent = tab.dataset.caption;
      this.ignite();
    }

    ignite() {
      const s = this.stage;
      s.classList.remove('is-off', 'is-igniting');
      void s.offsetWidth; // restart the animation
      s.classList.add('is-igniting');
      clearTimeout(this.igniteTimer);
      this.igniteTimer = setTimeout(() => s.classList.remove('is-igniting'), 900);
      this.power?.setAttribute('aria-pressed', 'true');
      if (this.power) this.power.lastElementChild.textContent = this.power.dataset.on;
      if (this.sound) this.sound.ignite(s.dataset.core);
    }

    toggleSound() {
      const on = this.soundBtn.getAttribute('aria-pressed') !== 'true';
      this.soundBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) {
        this.sound = this.sound || this.soundEngine || (this.soundEngine = new SaberSound());
        this.sound.ensure();
        this.sound.wake();
        // Lit already: ignite again so the ignition sound comes with an ignition on screen
        if (!this.stage.classList.contains('is-off')) this.ignite();
      } else if (this.sound) {
        this.sound.silence();
        this.sound = null;
      }
    }

    onSwing(e) {
      const now = performance.now();
      if (this.lastMove) {
        const dt = Math.max(8, now - this.lastMove.t);
        const v = Math.hypot(e.clientX - this.lastMove.x, e.clientY - this.lastMove.y) / dt; // px per ms
        const speed = Math.min(1, v / 2.2);
        if (speed > 0.18) {
          this.sound?.swing(speed);
          this.stage.classList.add('is-swinging');
          clearTimeout(this.swingTimer);
          this.swingTimer = setTimeout(() => this.stage.classList.remove('is-swinging'), 140);
        }
      }
      this.lastMove = { x: e.clientX, y: e.clientY, t: now };
    }

    retract() {
      clearTimeout(this.igniteTimer);
      this.stage.classList.remove('is-igniting');
      this.stage.classList.add('is-off');
      this.power?.setAttribute('aria-pressed', 'false');
      if (this.power) this.power.lastElementChild.textContent = this.power.dataset.off;
      if (this.sound) this.sound.retract();
    }
  }

  /* ---------------------------------------------------------- */
  /* Product rail arrows                                         */
  /* ---------------------------------------------------------- */
  class IgRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.ig-products--rail');
      this.prev = this.querySelector('.ig-rail-btn--prev');
      this.next = this.querySelector('.ig-rail-btn--next');
      if (!this.track) return;
      const step = () => this.track.clientWidth * 0.8;
      this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.next?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      const update = () => {
        if (this.prev) this.prev.disabled = this.track.scrollLeft < 8;
        if (this.next) this.next.disabled = this.track.scrollLeft + this.track.clientWidth > this.track.scrollWidth - 8;
      };
      this.track.addEventListener('scroll', update, { passive: true });
      update();
    }
  }

  /* ---------------------------------------------------------- */
  /* Saber Finder                                                */
  /* ---------------------------------------------------------- */
  const CORES = ['base', 'xeno', 'proffie'];
  const cap = s => s.charAt(0).toUpperCase() + s.slice(1);

  class IgFinder extends HTMLElement {
    connectedCallback() {
      this.console = this.querySelector('.ig-finder__console');
      this.steps = [...this.querySelectorAll('.ig-finder__step')];
      this.result = this.querySelector('[data-finder-result]');
      this.grid = this.querySelector('[data-finder-grid]');
      this.bars = [...this.querySelectorAll('.ig-finder__progress i')];
      this.count = this.querySelector('[data-finder-count]');
      this.backBtn = this.querySelector('[data-finder-back]');
      this.resetBtn = this.querySelector('[data-finder-reset]');
      this.answers = {};
      try {
        this.copy = JSON.parse(this.querySelector('script[data-finder-copy]').textContent);
      } catch (e) {
        this.copy = { core: { base: 'Base-Lit', xeno: 'Xenopixel', proffie: 'Proffie' }, level: { base: 1, xeno: 2, proffie: 3 }, why: {}, use: {}, show: {} };
      }

      this.steps.forEach((step, i) => {
        step.querySelectorAll('.ig-finder__opt').forEach(opt => {
          opt.setAttribute('aria-pressed', 'false');
          opt.addEventListener('click', () => this.answer(i, step.dataset.q, opt));
        });
      });
      this.backBtn?.addEventListener('click', () => this.show(Math.max(0, this.step - 1)));
      this.resetBtn?.addEventListener('click', () => {
        this.answers = {};
        this.querySelectorAll('.ig-finder__opt').forEach(o => o.setAttribute('aria-pressed', 'false'));
        this.show(0);
      });
      this.show(0, false);
    }

    answer(i, q, opt) {
      opt.parentElement.querySelectorAll('.ig-finder__opt').forEach(o => o.setAttribute('aria-pressed', o === opt ? 'true' : 'false'));
      this.answers[q] = opt.dataset.value;
      this.answers[`${q}Label`] = opt.querySelector('strong')?.textContent.trim();
      this.answers[`${q}Min`] = opt.dataset.min || '';
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.show(i + 1), reduceMotion.matches ? 0 : 280);
    }

    show(n, focus = true) {
      const total = this.steps.length;
      const done = n >= total;
      this.step = n;
      this.steps.forEach((s, k) => (s.hidden = k !== n));
      this.result.hidden = !done;
      this.console.dataset.step = n;
      this.bars.forEach((b, k) => b.classList.toggle('is-on', k <= n));
      this.count.textContent = done ? 'Complete' : `Step ${n + 1} of ${total}`;
      if (this.backBtn) this.backBtn.hidden = n === 0;
      if (this.resetBtn) this.resetBtn.hidden = n === 0;
      if (done) this.render();
      if (!focus) return;

      const target = done
        ? this.querySelector('[data-finder-focus]')
        : this.steps[n].querySelector('[aria-pressed="true"]') || this.steps[n].querySelector('.ig-finder__opt');
      target?.focus({ preventScroll: true });

      // Keep the console in view when its height changes (mostly on phones).
      const top = this.console.getBoundingClientRect().top;
      const behavior = reduceMotion.matches ? 'auto' : 'smooth';
      const sheet = this.closest('.ig-quiz__body');
      if (sheet) {
        const edge = sheet.getBoundingClientRect().top;
        if (top < edge) sheet.scrollBy({ top: top - edge - 12, behavior });
        return;
      }
      const header = parseFloat(getComputedStyle(root).getPropertyValue('--header-height')) || 72;
      if (top < header) window.scrollBy({ top: top - header - 12, behavior });
    }

    /* Price of a card for a given core, in cents. Cards without a core option only count as Base-Lit. */
    priceFor(card, core) {
      const own = card.dataset[`price${cap(core)}`];
      if (own) return parseInt(own, 10);
      const hasCores = CORES.some(c => card.dataset[`price${cap(c)}`]);
      return !hasCores && core === 'base' ? parseInt(card.dataset.price, 10) : null;
    }

    /* Up to `want` cards from the pools (in order) whose price for `core` sits between min and limit. */
    find(pools, core, limit, { min = 0, want = 3, seen = new Set() } = {}) {
      const out = [];
      for (const name of pools) {
        const pool = this.querySelector(`.ig-finder__pool[data-pool="${name}"]`);
        if (!pool) continue;
        for (const item of pool.querySelectorAll('.ig-finder__item')) {
          const card = item.querySelector('.ig-card');
          const key = card?.getAttribute('href');
          if (!card || seen.has(key)) continue;
          const price = this.priceFor(card, core);
          if (price === null || Number.isNaN(price)) continue;
          if ((limit > 0 && price > limit) || price < min) continue;
          seen.add(key);
          out.push({ item, pool, price, core });
          if (out.length >= want) return out;
        }
      }
      return out;
    }

    /* Cheapest price for a core across every pool, as the card prints it. */
    startsAt(core) {
      let best = null;
      this.querySelectorAll('.ig-finder__pool .ig-card').forEach(card => {
        const p = this.priceFor(card, core);
        if (p !== null && !Number.isNaN(p) && (!best || p < best.p)) best = { p };
      });
      if (best) {
        try {
          const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
          best.text = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(best.p / 100);
        } catch (e) {
          best.text = '';
        }
      }
      return best;
    }

    render() {
      const { use, show, budget, budgetMin } = this.answers;
      const limit = parseInt(budget, 10) || 0;
      const min = parseInt(budgetMin, 10) || 0;
      const steps = [...this.querySelectorAll('[data-q="budget"] .ig-finder__opt')].map(o => parseInt(o.dataset.value, 10));
      const low = steps[0] || 0;
      const high = steps[1] || 0;
      const name = c => this.copy.core[c] || c;

      // Which collections to look in, in order of preference
      // Built to use: sabers first. Full experience / all-around: sabers, then replicas. Iconic design: replicas first.
      let pools = ['neo', 'eco', 'replica'];
      if (use === 'iconic') pools = ['replica', 'neo', 'eco'];
      else if (use === 'experience' || use === 'allround') pools = ['neo', 'replica', 'eco'];
      if (use !== 'iconic' && show === 'base' && limit > 0 && limit <= low) pools = ['eco', 'neo', 'replica'];

      let core = show;
      let note = '';
      const seen = new Set();
      // "and up" looks for builds from the top budget step first
      let matches = budget === '0' && high ? this.find(pools, core, 0, { min: high, seen }) : [];
      // A price range ("$150–$300") looks inside the range first, then anything under its top
      if (min && limit > 0) matches = matches.concat(this.find(pools, core, limit, { min, seen }));
      if (matches.length < 3) matches = matches.concat(this.find(pools, core, limit, { want: 3 - matches.length, seen }));

      if (matches.length < 3 && limit > 0) {
        // Not enough in budget: fill with the next core down, so there is always something to compare
        const lower = CORES.slice(0, CORES.indexOf(show)).reverse();
        for (const c of lower) {
          if (matches.length >= 3) break;
          matches = matches.concat(this.find(pools, c, limit, { want: 3 - matches.length, seen }));
        }
        if (!matches.some(m => m.core === show) && matches.length) {
          core = matches[0].core;
          const from = this.startsAt(show);
          note = from && from.text
            ? `${name(show)} builds start at ${from.text}, so these are the closest matches in your budget.`
            : `${name(show)} builds sit above that budget, so these are the closest matches.`;
        }
      }
      if (!matches.length) {
        matches = this.find(pools, show, 0, { want: 12 }).sort((a, b) => a.price - b.price).slice(0, 3);
        core = show;
        if (matches.length && limit > 0) note = 'Nothing fits that budget right now, so these are the most affordable matches.';
      }

      this.querySelector('[data-finder-core]').textContent = name(core);
      this.querySelector('[data-finder-level]').dataset.level = this.copy.level[core] || 1;
      const wrap = this.querySelector('[data-finder-core-wrap]');
      if (wrap) wrap.dataset.core = core;
      const picksTitle = this.querySelector('[data-finder-picks-title]');
      if (picksTitle) {
        const base = this.copy.picks || 'Our top 3 for you';
        picksTitle.hidden = !matches.length;
        picksTitle.textContent = matches.length === 1 ? base.replace(/top 3/i, 'top pick') : base.replace(/\b3\b/, String(matches.length || 3));
      }
      this.querySelector('[data-finder-why]').textContent = this.copy.why[core] || '';
      const noteEl = this.querySelector('[data-finder-note]');
      noteEl.textContent = note;
      noteEl.hidden = !note;

      const chips = [this.copy.use[use], this.answers.showLabel && this.copy.show[show], this.answers.budgetLabel].filter(Boolean);
      this.querySelector('[data-finder-answers]').replaceChildren(...chips.map(t => Object.assign(document.createElement('li'), { textContent: t })));

      const all = this.querySelector('[data-finder-all]');
      const firstPool = (matches[0]?.pool) || this.querySelector(`.ig-finder__pool[data-pool="${pools[0]}"]`);
      if (all && firstPool) all.href = firstPool.dataset.url;

      if (matches.length) {
        this.grid.replaceChildren(...matches.map((m, i) => {
          const clone = m.item.cloneNode(true);
          clone.style.setProperty('--i', i);
          clone.dataset.corePick = m.core; // highlights the matching price on the card
          return clone;
        }));
      } else {
        const empty = Object.assign(document.createElement('p'), { className: 'ig-finder__empty', textContent: 'We could not find a match right now. Browse the full range instead.' });
        this.grid.replaceChildren(empty);
      }
    }
  }

  /* ---------------------------------------------------------- */
  /* Sound font player: the real AllySabers sounds               */
  /* ---------------------------------------------------------- */
  // Plays a saber sound font through Web Audio: looping hum, two smooth-swing layers that rise with swing
  // speed, and one-shots for ignition, retraction, clash, blaster and force. Drag and lockup loop while held.
  // Files load only when someone turns sound on. If anything fails to load, the synthesized SaberSound
  // takes over, so there is always sound.
  class SaberFont {
    constructor(urls) {
      this.urls = urls || {};
      this.continuous = true;
      this.buffers = {};
      this.loops = {};
      this.lit = false;
    }

    ensure() {
      if (this.ctx) return true;
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return false;
      const ctx = (this.ctx = new AC());
      this.out = ctx.createGain();
      this.level = 0.9;
      this.out.gain.value = this.level;
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -12;
      comp.knee.value = 6;
      comp.ratio.value = 6;
      comp.attack.value = 0.003;
      comp.release.value = 0.2;
      // Master volume after the limiter (the limiter adds its own make-up gain): about -10 dB from before,
      // so the first ignition doesn't blast anyone's ears
      const master = ctx.createGain();
      master.gain.value = 0.32;
      this.analyser = ctx.createAnalyser();
      this.analyser.fftSize = 1024;
      // the scope and blade flicker read the signal before the volume, so the visuals keep their size
      this.out.connect(comp);
      comp.connect(this.analyser);
      comp.connect(master).connect(ctx.destination);
      this.load();
      return true;
    }

    load() {
      if (this.loading) return this.loading;
      const get = (name, url) => fetch(url)
        .then(r => { if (!r.ok) throw new Error(r.status); return r.arrayBuffer(); })
        .then(b => new Promise((ok, no) => this.ctx.decodeAudioData(b, ok, no)))
        .then(buf => { this.buffers[name] = buf; });
      const need = ['hum', 'out', 'in'];
      this.loading = Promise.all(Object.entries(this.urls).map(([k, u]) => get(k, u).catch(e => { if (need.includes(k)) throw e; })))
        .then(() => { this.ready = true; })
        .catch(() => { this.failed = true; });
      return this.loading;
    }

    resume() {
      if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume();
    }

    play(name, gain = 1, rate = 1) {
      const buf = this.buffers[name];
      if (!buf) return null;
      const s = this.ctx.createBufferSource();
      const g = this.ctx.createGain();
      s.buffer = buf;
      s.playbackRate.value = rate;
      g.gain.value = gain;
      s.connect(g).connect(this.out);
      s.start();
      return { s, g };
    }

    // A looping layer that runs silently until its gain is raised
    layer(name, gain = 0, rate = 1) {
      const buf = this.buffers[name];
      if (!buf) return null;
      const s = this.ctx.createBufferSource();
      const g = this.ctx.createGain();
      s.buffer = buf;
      s.loop = true;
      s.playbackRate.value = rate;
      g.gain.value = gain;
      s.connect(g).connect(this.out);
      s.start(this.ctx.currentTime, Math.random() * buf.duration * 0.5);
      return { s, g };
    }

    stopLayer(l, fade = 0.08) {
      if (!l) return;
      const t = this.ctx.currentTime;
      l.g.gain.cancelScheduledValues(t);
      l.g.gain.setTargetAtTime(0, t, fade / 3);
      l.s.stop(t + fade + 0.1);
    }

    ignite() {
      if (!this.ensure()) return;
      this.resume();
      this.lit = true;
      this.play('out', 0.95);
      const t = this.ctx.currentTime;
      if (!this.hum) {
        this.hum = this.layer('hum', 0);
        this.swingA = this.layer('swingA', 0);
        this.swingB = this.layer('swingB', 0);
      }
      if (this.hum) {
        this.hum.g.gain.cancelScheduledValues(t);
        this.hum.g.gain.setValueAtTime(this.hum.g.gain.value, t);
        this.hum.g.gain.setTargetAtTime(0.85, t + 0.25, 0.18);
      }
    }

    retract() {
      if (!this.ctx) return;
      this.lit = false;
      this.play('in', 0.95);
      ['hum', 'swingA', 'swingB'].forEach(k => { this.stopLayer(this[k], 0.45); this[k] = null; });
      Object.keys(this.loops).forEach(k => this.loop(k, false));
      this.texture(0);
    }

    // speed 0..1, called every frame while lit (smooth swing)
    swing(speed) {
      if (!this.ctx || !this.lit || !this.hum) return;
      const t = this.ctx.currentTime;
      const a = Math.pow(speed, 1.3) * 1.1;
      const b = Math.pow(Math.max(0, speed - 0.35) / 0.65, 1.4) * 0.9;
      this.swingA?.g.gain.setTargetAtTime(a, t, 0.035);
      this.swingB?.g.gain.setTargetAtTime(b, t, 0.035);
      this.hum.g.gain.setTargetAtTime(0.85 * (1 - 0.55 * Math.min(1, speed * 1.4)), t, 0.05);
    }

    clash() {
      if (!this.ctx || !this.lit) return;
      this.resume();
      this.play('clash', 1, 0.97 + Math.random() * 0.06);
    }

    blaster() {
      if (!this.ctx || !this.lit) return;
      this.resume();
      this.play('blaster', 0.95, 0.97 + Math.random() * 0.06);
    }

    force() {
      if (!this.ctx || !this.lit) return;
      this.resume();
      this.play('force', 1);
    }

    // Held effects. Tip drag uses the font's drag loop; lockup is built from the same material,
    // pitched down and pulsed, with a clash on contact.
    loop(kind, on) {
      if (!this.ctx) return;
      if (on && !this.loops[kind] && this.lit) {
        this.resume();
        const ctx = this.ctx;
        const t = ctx.currentTime;
        const l = this.layer('drag', 0, kind === 'lockup' ? 0.72 : 1);
        if (!l) return;
        l.g.disconnect();
        const f = ctx.createBiquadFilter();
        f.type = kind === 'lockup' ? 'bandpass' : 'highpass';
        f.frequency.value = kind === 'lockup' ? 1500 : 500;
        f.Q.value = kind === 'lockup' ? 0.8 : 0.5;
        const am = ctx.createGain();
        am.gain.value = kind === 'lockup' ? 0.6 : 1;
        if (kind === 'lockup') {
          const lfo = ctx.createOscillator();
          const depth = ctx.createGain();
          lfo.type = 'square';
          lfo.frequency.value = 17;
          depth.gain.value = 0.4;
          lfo.connect(depth).connect(am.gain);
          lfo.start();
          l.lfo = lfo;
        }
        l.g.connect(f).connect(am).connect(this.out);
        l.g.gain.setTargetAtTime(kind === 'lockup' ? 1.6 : 1.1, t, 0.02);
        this.loops[kind] = l;
        if (kind === 'lockup') this.clash();
      } else if (!on && this.loops[kind]) {
        const l = this.loops[kind];
        this.stopLayer(l, 0.12);
        l.lfo?.stop(this.ctx.currentTime + 0.3);
        delete this.loops[kind];
      }
    }

    // Unstable / fire styles: a low crackle from the drag loop under the hum
    texture(level) {
      if (!this.ctx) return;
      if (level > 0 && this.lit && this.buffers.drag) {
        if (!this.crackle) {
          const l = this.layer('drag', 0, 0.5);
          if (!l) return;
          l.g.disconnect();
          const f = this.ctx.createBiquadFilter();
          f.type = 'lowpass';
          f.frequency.value = 1100;
          l.g.connect(f).connect(this.out);
          this.crackle = l;
        }
        this.crackle.g.gain.setTargetAtTime(level * 7, this.ctx.currentTime, 0.1);
      } else if (this.crackle) {
        this.stopLayer(this.crackle, 0.2);
        this.crackle = null;
      }
    }

    silence() {
      if (!this.ctx) return;
      clearTimeout(this.sleepTimer);
      const t = this.ctx.currentTime;
      this.out.gain.setTargetAtTime(0, t, 0.05);
      this.sleepTimer = setTimeout(() => this.ctx.state === 'running' && this.ctx.suspend(), 350);
    }

    wake() {
      if (!this.ctx) return;
      clearTimeout(this.sleepTimer);
      this.resume();
      this.out.gain.setTargetAtTime(this.level || 0.9, this.ctx.currentTime, 0.1);
    }
  }

  /* ---------------------------------------------------------- */
  /* Core compare: pick a core, charge the bars, explain rows    */
  /* ---------------------------------------------------------- */
  class IgCores extends HTMLElement {
    connectedCallback() {
      this.picks = [...this.querySelectorAll('.ig-cores__pick')];
      this.panels = [...this.querySelectorAll('.ig-cores__panel')];
      this.readout = this.querySelector('[data-readout]');
      this.chart = this.querySelector('.ig-cores__chart');
      this.table = this.querySelector('.ig-cores__table');
      this.demo = this.querySelector('.ig-cores__demo');
      this.views = [...this.querySelectorAll('.ig-cores__view')];
      this.tip = this.querySelector('.ig-cores__tip');
      this.out = this.querySelector('[data-explain-out]');
      this.hint = this.out ? this.out.textContent : '';
      try {
        this.captions = JSON.parse(this.querySelector('script[data-captions]').textContent);
      } catch (e) {
        this.captions = {};
      }

      // One core is always highlighted: the buttons behave like radio buttons.
      this.picks.forEach(btn => btn.addEventListener('click', () => {
        if (btn.getAttribute('aria-pressed') !== 'true') this.pick(btn.dataset.core);
      }));

      this.views.forEach((tab, i) => {
        tab.addEventListener('click', () => this.view(tab.dataset.view));
        tab.addEventListener('keydown', e => {
          const n = this.views.length;
          const to = { ArrowRight: (i + 1) % n, ArrowLeft: (i - 1 + n) % n, Home: 0, End: n - 1 }[e.key];
          if (to === undefined) return;
          e.preventDefault();
          const next = this.views[to];
          next.focus();
          this.view(next.dataset.view);
        });
      });

      this.querySelectorAll('.ig-cores__metric').forEach(m => m.addEventListener('click', () => this.explain(m)));

      // Product page: Choose buttons + live side-by-side table. Deferred a tick so the helpers defined further down
      // this file exist when the element upgrades.
      if (this.dataset.productId) {
        queueMicrotask(() => {
          this.build = new BuildCompare(this, core => {
            if (this.dataset.pick !== core) this.pick(core);
          });
        });
      }

      this.querySelectorAll('.ig-cores__meter').forEach(m => {
        m.addEventListener('pointerenter', () => this.showTip(m));
        m.addEventListener('pointerleave', () => this.hideTip());
      });

      // Bars charge up the first time the chart comes into view
      if ('IntersectionObserver' in window && !reduceMotion.matches) {
        const io = new IntersectionObserver(entries => {
          if (entries.some(e => e.isIntersecting)) { io.disconnect(); this.charge(); }
        }, { threshold: 0.3 });
        io.observe(this.chart);
      } else {
        this.chart.classList.add('is-on');
      }
    }

    charge() {
      const c = this.chart;
      c.classList.remove('is-on');
      void c.offsetWidth;
      c.classList.add('is-on');
    }

    pick(core) {
      this.dataset.pick = core;
      this.picks.forEach(b => b.setAttribute('aria-pressed', b.dataset.core === core ? 'true' : 'false'));
      this.panels.forEach(p => (p.hidden = p.dataset.panel !== core));
      const btn = this.picks.find(b => b.dataset.core === core);
      if (this.readout) this.readout.textContent = btn ? btn.dataset.name : 'All cores';
      if (!this.chart.hidden) this.charge();

      // Keep the ignite demo on the same core
      const lab = this.demo;
      const key = core === 'all' ? 'base' : core;
      if (lab && lab.setCore) {
        const named = this.picks.find(b => b.dataset.core === key);
        lab.setCore(key, named && named.dataset.name, this.captions[key] || '');
      } else if (lab && lab.stage) {
        lab.stage.dataset.core = key;
        const named = this.picks.find(b => b.dataset.core === key);
        if (lab.coreLabel && named) lab.coreLabel.textContent = named.dataset.name;
        if (lab.caption) lab.caption.textContent = this.captions[key] || '';
        if (!lab.hidden) lab.ignite();
      }
    }

    view(name) {
      this.views.forEach(v => {
        const on = v.dataset.view === name;
        v.setAttribute('aria-selected', on ? 'true' : 'false');
        v.tabIndex = on ? 0 : -1;
      });
      this.chart.hidden = name !== 'chart';
      if (this.table) this.table.hidden = name !== 'table';
      if (this.demo) this.demo.hidden = name !== 'demo';
      this.hideTip();
      if (this.demo && this.demo.activate) {
        if (name === 'demo') this.demo.activate();
        else { this.demo.deactivate(); if (name === 'chart') this.charge(); }
      } else if (name === 'demo' && this.demo && this.demo.ignite) {
        this.demo.io?.disconnect();
        this.demo.stage.classList.add('is-off');
        setTimeout(() => this.demo.ignite(), 120);
      } else if (name === 'chart') {
        this.charge();
      }
    }

    explain(btn) {
      const open = btn.getAttribute('aria-expanded') === 'true';
      this.querySelectorAll('.ig-cores__metric').forEach(m => m.setAttribute('aria-expanded', 'false'));
      if (!this.out) return;
      if (open) {
        this.out.textContent = this.hint;
        return;
      }
      btn.setAttribute('aria-expanded', 'true');
      const label = document.createElement('b');
      label.textContent = btn.querySelector('span').textContent;
      this.out.replaceChildren(label, document.createTextNode(` · ${btn.dataset.explain}`));
    }

    showTip(meter) {
      if (!this.tip) return;
      const r = meter.querySelector('.ig-cores__cells').getBoundingClientRect();
      const c = this.chart.getBoundingClientRect();
      this.tip.textContent = meter.dataset.tip;
      this.tip.hidden = false;
      this.tip.style.left = `${r.right - c.left}px`;
      this.tip.style.top = `${r.top - c.top}px`;
    }

    hideTip() {
      if (this.tip) this.tip.hidden = true;
    }
  }

  /* ---------------------------------------------------------- */
  /* Hyperspace footer: drifting starfield + jump to lightspeed  */
  /* ---------------------------------------------------------- */
  const spawnStar = fresh => ({ x: Math.random() * 2 - 1, y: Math.random() * 2 - 1, z: fresh ? 1 : Math.random() * 0.98 + 0.02 });

  // Full-screen warp used by the "Jump to lightspeed" button: streaks accelerate, the page jumps to the top
  // behind them, then the overlay fades away.
  const lightspeed = () => {
    if (reduceMotion.matches) {
      window.scrollTo({ top: 0 });
      return;
    }
    const layer = document.createElement('canvas');
    layer.className = 'ig-warp';
    layer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(layer);
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = window.innerWidth;
    const h = window.innerHeight;
    layer.width = w * dpr;
    layer.height = h * dpr;
    const ctx = layer.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const stars = Array.from({ length: 420 }, () => spawnStar(false));
    const scale = Math.max(w, h) * 0.5;
    const start = performance.now();
    let jumped = false;

    const draw = now => {
      const t = now - start;
      // speed ramps up for 700ms, holds, then the overlay fades out
      const ramp = Math.min(1, t / 700);
      const speed = 0.004 + ramp * ramp * 0.05;
      ctx.fillStyle = `rgba(3, 4, 6, ${0.25 + ramp * 0.35})`;
      ctx.fillRect(0, 0, w, h);
      ctx.lineCap = 'round';
      for (const s of stars) {
        const pz = s.z;
        s.z -= speed;
        if (s.z <= 0.02) Object.assign(s, spawnStar(true));
        const sx = w / 2 + (s.x / s.z) * scale;
        const sy = h / 2 + (s.y / s.z) * scale;
        const px = w / 2 + (s.x / pz) * scale;
        const py = h / 2 + (s.y / pz) * scale;
        const a = Math.min(1, (1 - s.z) * 1.4);
        ctx.strokeStyle = `rgba(${190 + Math.round(65 * (1 - s.z))}, 225, 255, ${a})`;
        ctx.lineWidth = (1 - s.z) * 2.4 + 0.3;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(sx, sy);
        ctx.stroke();
      }
      // a cool blue bloom at the vanishing point just before the jump
      if (t > 350 && t < 900) {
        const k = Math.sin(((t - 350) / 550) * Math.PI);
        const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) * 0.45);
        g.addColorStop(0, `rgba(200, 230, 255, ${0.22 * k})`);
        g.addColorStop(0.35, `rgba(65, 176, 255, ${0.12 * k})`);
        g.addColorStop(1, 'rgba(65, 176, 255, 0)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      }
      if (!jumped && t > 650) {
        jumped = true;
        try {
          window.scrollTo({ top: 0, behavior: 'instant' });
        } catch (e) {
          window.scrollTo(0, 0);
        }
        layer.classList.add('is-out');
      }
      if (t < 1500) requestAnimationFrame(draw);
      else layer.remove();
    };
    requestAnimationFrame(draw);
  };

  class IgStarfield extends HTMLElement {
    connectedCallback() {
      this.canvas = this.querySelector('canvas');
      if (!this.canvas || !this.canvas.getContext) return;
      this.ctx = this.canvas.getContext('2d');
      this.footer = this.closest('.ig-footer') || this.parentElement;
      this.stars = Array.from({ length: 170 }, () => spawnStar(false));
      this.mx = 0;
      this.my = 0;
      this.tx = 0;
      this.ty = 0;
      this.frame = this.frame.bind(this);

      if ('ResizeObserver' in window) new ResizeObserver(() => this.resize()).observe(this);
      this.resize();

      this.footer.addEventListener('pointermove', e => {
        const r = this.getBoundingClientRect();
        this.tx = (e.clientX - r.left) / r.width - 0.5;
        this.ty = (e.clientY - r.top) / r.height - 0.5;
      });

      this.footer.querySelectorAll('[data-lightspeed]').forEach(btn => btn.addEventListener('click', () => {
        this.footer.classList.add('is-jumping');
        lightspeed();
        setTimeout(() => this.footer.classList.remove('is-jumping'), 1500);
      }));

      if (reduceMotion.matches || !('IntersectionObserver' in window)) {
        this.draw(0);
        return;
      }
      new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible && !this.raf) this.raf = requestAnimationFrame(this.frame);
      }).observe(this);
    }

    resize() {
      const r = this.getBoundingClientRect();
      this.dpr = Math.min(2, window.devicePixelRatio || 1);
      this.w = r.width;
      this.h = r.height;
      this.canvas.width = Math.round(r.width * this.dpr);
      this.canvas.height = Math.round(r.height * this.dpr);
      if (reduceMotion.matches) this.draw(0);
    }

    draw(dt) {
      const { ctx, w, h, dpr } = this;
      if (!w || !h) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      this.mx += (this.tx - this.mx) * 0.05;
      this.my += (this.ty - this.my) * 0.05;
      // vanishing point sits low, behind the wordmark, and leans toward the pointer
      const cx = w * (0.5 + this.mx * 0.08);
      const cy = h * (0.78 + this.my * 0.06);
      const scale = Math.max(w, h) * 0.42;
      const speed = 0.00006 * dt;
      for (const s of this.stars) {
        s.z -= speed;
        if (s.z <= 0.02) Object.assign(s, spawnStar(true));
        const sx = cx + (s.x / s.z) * scale;
        const sy = cy + (s.y / s.z) * scale * 0.7;
        if (sx < -10 || sx > w + 10 || sy < -10 || sy > h + 10) {
          Object.assign(s, spawnStar(true));
          continue;
        }
        const depth = 1 - s.z;
        const size = depth * 1.6 + 0.25;
        ctx.fillStyle = `rgba(214, 230, 255, ${Math.min(0.9, depth * 1.1 + 0.08)})`;
        ctx.beginPath();
        ctx.arc(sx, sy, size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    frame(now) {
      const dt = Math.min(48, now - (this.last || now));
      this.last = now;
      this.draw(dt);
      this.raf = this.visible ? requestAnimationFrame(this.frame) : null;
      if (!this.visible) this.last = null;
    }
  }

  /* ---------------------------------------------------------- */
  /* Blade lab: a real-time saber simulator for the core demo    */
  /* ---------------------------------------------------------- */
  // Drawn on canvas every frame: a pixel-by-pixel blade (white-hot core, coloured bloom), a machined hilt,
  // light spilling onto the floor and the haze, sparks, and swing trails. Each core behaves like the real
  // board: Base-Lit lights and reacts as one piece from the hilt; Xenopixel and Proffie ignite hilt to tip
  // and put effects exactly where they happen; Proffie adds a custom style.

  const LAB_CORES = {
    base: { ignite: 'fade', local: false, fx: ['clash', 'blaster', 'lockup', 'force'], styles: ['stable'], source: 'Single LED in the hilt', strip: 'Hilt LED' },
    xeno: { ignite: 'scroll', local: true, fx: ['clash', 'blaster', 'lockup', 'drag', 'force'], styles: ['stable', 'unstable', 'fire', 'rainbow'], source: 'Pixel strip inside the blade', strip: 'Pixel map' },
    proffie: { ignite: 'spark', local: true, fx: ['clash', 'blaster', 'lockup', 'drag', 'force'], styles: ['stable', 'unstable', 'fire', 'rainbow', 'custom'], source: 'Programmable pixel strip', strip: 'Pixel map' }
  };
  const LAB_NAMES = { base: 'Base-Lit', xeno: 'Xenopixel', proffie: 'Proffie' };
  // Canvas blur filters give the smoothest bloom; browsers without them get a layered fallback
  const LAB_BLUR = (() => {
    try {
      const t = document.createElement('canvas').getContext('2d');
      t.filter = 'blur(1px)';
      return t.filter === 'blur(1px)';
    } catch (e) {
      return false;
    }
  })();
  const FX_NAMES = { clash: 'Clash', blaster: 'Blaster', lockup: 'Lockup', drag: 'Tip drag', force: 'Force' };
  const STYLE_NAMES = { stable: 'Stable', unstable: 'Unstable', fire: 'Fire', rainbow: 'Rainbow', custom: 'Custom' };

  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  const hexRgb = hex => {
    const n = parseInt(hex.replace('#', ''), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  const hueRgb = h => {
    const f = n => {
      const k = (n + h / 60) % 6;
      return 255 * (1 - Math.max(0, Math.min(k, 4 - k, 1)));
    };
    return [f(5), f(3), f(1)];
  };
  // Smooth 1D value noise, 0..1
  const hash = n => {
    const x = Math.sin(n * 127.1) * 43758.5453;
    return x - Math.floor(x);
  };
  const noise = x => {
    const i = Math.floor(x);
    const f = x - i;
    const u = f * f * (3 - 2 * f);
    return hash(i) * (1 - u) + hash(i + 1) * u;
  };

  class IgBladeLab extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.stage = this.querySelector('[data-bench-stage]');
      this.canvas = this.querySelector('.ig-bench__canvas');
      this.stripCanvas = this.querySelector('[data-bench-strip]');
      this.scopeCanvas = this.querySelector('[data-bench-scope]');
      if (!this.stage || !this.canvas || !this.canvas.getContext) return;
      this.ctx = this.canvas.getContext('2d');
      this.coreLabel = this.querySelector('[data-core-label]');
      this.sourceLabel = this.querySelector('[data-source-label]');
      this.styleLabel = this.querySelector('[data-style-label]');
      this.colorLabel = this.querySelector('[data-color-label]');
      this.stripLabel = this.querySelector('[data-strip-label]');
      this.caption = this.querySelector('[data-lab-caption]');
      this.statusEl = this.querySelector('[data-bench-status]');
      this.hintEl = this.querySelector('[data-bench-hint]');
      this.swingBars = [...this.querySelectorAll('[data-bench-swing] i')];
      this.powerBtn = this.querySelector('[data-power]');
      this.fxBtns = [...this.querySelectorAll('[data-fx]')];
      this.styleBtns = [...this.querySelectorAll('[data-style]')];
      this.swatches = [...this.querySelectorAll('[data-color]')];
      this.soundBtn = this.querySelector('[data-sound]');

      this.core = LAB_CORES[this.dataset.core] ? this.dataset.core : 'xeno';
      this.bladeStyle = 'stable';
      this.rgb = hexRgb(this.swatches[0]?.dataset.color || '#2f8fff');
      this.target = this.rgb.slice();
      this.on = false;
      this.level = 0; // ignition progress 0..1
      this.motion = 0; // 1 igniting, -1 retracting
      this.flash = 0;
      this.events = [];
      this.holds = {};
      this.sparks = [];
      this.trail = [];
      this.ang = 0;
      this.vel = 0;
      this.aim = null;
      this.time = 0;
      this.touch = matchMedia('(hover: none)').matches;

      this.powerBtn?.addEventListener('click', () => (this.on ? this.retract() : this.ignite()));
      this.fxBtns.forEach(btn => {
        const fx = btn.dataset.fx;
        if (btn.hasAttribute('data-hold')) {
          const start = e => { e.preventDefault(); this.hold(fx, true, btn); };
          const stop = () => this.hold(fx, false, btn);
          btn.addEventListener('pointerdown', start);
          ['pointerup', 'pointerleave', 'pointercancel'].forEach(t => btn.addEventListener(t, stop));
          btn.addEventListener('keydown', e => { if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); this.hold(fx, true, btn); } });
          btn.addEventListener('keyup', e => { if (e.key === ' ' || e.key === 'Enter') this.hold(fx, false, btn); });
          btn.addEventListener('contextmenu', e => e.preventDefault());
        } else {
          btn.addEventListener('click', () => this.trigger(fx));
        }
      });
      this.styleBtns.forEach(btn => btn.addEventListener('click', () => this.setStyle(btn.dataset.style)));
      this.swatches.forEach(sw => sw.addEventListener('click', () => {
        this.swatches.forEach(s => s.setAttribute('aria-pressed', s === sw ? 'true' : 'false'));
        this.target = hexRgb(sw.dataset.color);
        this.style.setProperty('--lab', sw.dataset.color);
        if (this.colorLabel) this.colorLabel.textContent = sw.dataset.name;
        this.bladeStyle === 'rainbow' && this.setStyle('stable');
      }));

      if (this.soundBtn) {
        if (window.AudioContext || window.webkitAudioContext) this.soundBtn.addEventListener('click', () => this.toggleSound());
        else this.soundBtn.hidden = true;
      }

      // Pointer: the saber follows the pointer on desktop; on touch, drag sideways to swing. Tap/click the blade to clash.
      const st = this.stage;
      st.addEventListener('pointermove', e => this.onMove(e));
      st.addEventListener('pointerleave', () => { if (!this.touch) this.aim = null; });
      st.addEventListener('pointerdown', e => this.onDown(e));
      st.addEventListener('pointerup', e => this.onUp(e));
      st.addEventListener('pointercancel', () => { this.drag = null; });

      this.resize = this.resize.bind(this);
      if ('ResizeObserver' in window) new ResizeObserver(this.resize).observe(st);
      else window.addEventListener('resize', this.resize);

      this.visible = false;
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.visible ? this.wake() : this.sleep();
        }, { threshold: 0.2 }).observe(st);
      } else {
        this.visible = true;
      }
      document.addEventListener('visibilitychange', () => (document.hidden ? this.sleep() : this.wake()));

      this.setCore(this.core, null, null, true);
      this.hint(this.touch ? 'Drag to swing · tap the blade · try the sound' : 'Point to swing · click the blade to clash · turn on sound for our real sound font');
      this.resize();
      this.frame = this.frame.bind(this);
    }

    /* ---- public API used by the core compare section ---- */

    setCore(core, name, caption, silent) {
      if (!LAB_CORES[core]) return;
      const changed = core !== this.core;
      this.core = core;
      this.dataset.core = core;
      const caps = LAB_CORES[core];
      if (this.coreLabel) this.coreLabel.textContent = name || LAB_NAMES[core];
      if (this.sourceLabel) this.sourceLabel.textContent = caps.source;
      if (this.stripLabel) this.stripLabel.textContent = caps.strip;
      if (caption != null && this.caption) this.caption.textContent = caption;
      this.fxBtns.forEach(b => {
        const ok = caps.fx.includes(b.dataset.fx);
        b.toggleAttribute('data-locked', !ok);
        b.setAttribute('aria-disabled', ok ? 'false' : 'true');
        b.title = ok ? '' : `${FX_NAMES[b.dataset.fx]} needs a pixel blade (Xenopixel or Proffie)`;
      });
      this.styleBtns.forEach(b => {
        const ok = caps.styles.includes(b.dataset.style);
        b.toggleAttribute('data-locked', !ok);
        b.setAttribute('aria-disabled', ok ? 'false' : 'true');
      });
      if (!caps.styles.includes(this.bladeStyle)) this.setStyle('stable', true);
      Object.keys(this.holds).forEach(k => this.hold(k, false));
      if (!silent && changed && !this.hidden) {
        this.level = 0;
        this.on = false;
        this.ignite();
      }
    }

    activate() {
      this.shown = true;
      this.autoIgnited = true;
      this.resize();
      this.wake();
      this.level = 0;
      this.on = false;
      clearTimeout(this.autoTimer);
      this.autoTimer = setTimeout(() => this.ignite(), 180);
    }

    deactivate() {
      this.shown = false;
      Object.keys(this.holds).forEach(k => this.hold(k, false));
      this.sleep();
    }

    ignite() {
      this.on = true;
      this.motion = 1;
      if (reduceMotion.matches) this.level = 1;
      this.setPower();
      this.sound?.ignite(this.core);
      this.sound?.texture(this.bladeStyle === 'unstable' ? 0.06 : this.bladeStyle === 'fire' ? 0.03 : 0);
      this.wake();
    }

    retract() {
      this.on = false;
      this.motion = -1;
      Object.keys(this.holds).forEach(k => this.hold(k, false));
      this.setPower();
      this.sound?.retract();
      this.sound?.texture(0);
      if (reduceMotion.matches) this.level = 0;
    }

    /* ---- controls ---- */

    setPower() {
      if (!this.powerBtn) return;
      this.powerBtn.setAttribute('aria-pressed', String(this.on));
      const label = this.powerBtn.querySelector('[data-power-label]');
      if (label) label.textContent = this.on ? 'Retract' : 'Ignite';
    }

    setStyle(style, silent) {
      const caps = LAB_CORES[this.core];
      if (!caps.styles.includes(style)) {
        this.locked(style === 'custom' ? 'proffie' : 'xeno', style === 'custom' ? 'Custom styles are written on Proffie' : `${STYLE_NAMES[style]} needs a pixel blade`);
        return;
      }
      this.bladeStyle = style;
      this.styleBtns.forEach(b => b.setAttribute('aria-pressed', String(b.dataset.style === style)));
      if (this.styleLabel) this.styleLabel.textContent = STYLE_NAMES[style];
      this.sound?.texture(style === 'unstable' ? 0.06 : style === 'fire' ? 0.03 : 0);
      if (!silent) this.status(`Style · ${STYLE_NAMES[style]}`);
    }

    trigger(fx, pos) {
      const caps = LAB_CORES[this.core];
      if (!caps.fx.includes(fx)) { this.locked('xeno', `${FX_NAMES[fx]} needs a pixel blade`); return; }
      if (!this.on) this.ignite();
      if (this.level < 0.95) return;
      this.hint('');
      if (fx === 'clash') {
        const p = pos != null ? pos : 0.55 + Math.random() * 0.3;
        this.events.push({ type: 'clash', pos: p, t0: this.time });
        this.vel += (Math.random() < 0.5 ? -1 : 1) * 2.4;
        this.spray(p, 22, 1);
        this.sound?.clash();
        this.status('Clash');
      } else if (fx === 'blaster') {
        const p = 0.3 + Math.random() * 0.5;
        this.events.push({ type: 'blaster', pos: p, t0: this.time });
        this.vel += (Math.random() < 0.5 ? -1 : 1) * 0.9;
        this.spray(p, 8, 0.6);
        this.sound?.blaster();
        this.status('Blaster deflected');
      } else if (fx === 'force') {
        this.events.push({ type: 'force', pos: 0, t0: this.time });
        this.shake = 1;
        this.vel += (Math.random() < 0.5 ? -1 : 1) * 1.2;
        // push the haze away from the blade
        if (this.haze && this.pivot) {
          const mid = this.x0 + this.len * 0.5;
          const mx = this.pivot.x + Math.cos(this.ang) * mid;
          const my = this.pivot.y + Math.sin(this.ang) * mid;
          this.haze.forEach(p => {
            const dx = p.x - mx, dy = p.y - my, d = Math.hypot(dx, dy) || 1;
            const f = 420 / (1 + d / 120);
            p.ix = (p.ix || 0) + (dx / d) * f;
            p.iy = (p.iy || 0) + (dy / d) * f;
          });
        }
        this.sound?.force?.();
        this.status('Force push');
      }
    }

    hold(fx, on, btn) {
      const caps = LAB_CORES[this.core];
      if (on && !caps.fx.includes(fx)) { this.locked('xeno', `${FX_NAMES[fx]} needs a pixel blade`); return; }
      if (on && !this.on) this.ignite();
      if (!!this.holds[fx] === on) return;
      if (on) {
        this.holds[fx] = { pos: fx === 'drag' ? 1 : 0.58 + Math.random() * 0.12 };
        this.status(FX_NAMES[fx]);
        this.hint('');
      } else {
        delete this.holds[fx];
      }
      (btn || this.fxBtns.find(b => b.dataset.fx === fx))?.classList.toggle('is-active', on);
      this.sound?.loop(fx, on);
    }

    locked(core, message) {
      if (!this.statusEl) return;
      clearTimeout(this.statusTimer);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ig-bench__switch';
      btn.textContent = `Try it on ${LAB_NAMES[core]}`;
      btn.addEventListener('click', () => {
        const host = this.closest('ig-cores');
        if (host && host.pick) host.pick(core);
        else this.setCore(core);
      });
      this.statusEl.replaceChildren(document.createTextNode(`${message}. `), btn);
      this.statusEl.classList.add('is-on', 'is-locked');
      this.statusTimer = setTimeout(() => this.statusEl.classList.remove('is-on', 'is-locked'), 4200);
    }

    status(text) {
      if (!this.statusEl) return;
      clearTimeout(this.statusTimer);
      this.statusEl.textContent = text;
      this.statusEl.classList.remove('is-locked');
      this.statusEl.classList.add('is-on');
      this.statusTimer = setTimeout(() => this.statusEl.classList.remove('is-on'), 1500);
    }

    hint(text) {
      if (!this.hintEl) return;
      if (text) this.hintEl.textContent = text;
      this.hintEl.classList.toggle('is-on', !!text);
    }

    toggleSound() {
      const on = this.soundBtn.getAttribute('aria-pressed') !== 'true';
      this.soundBtn.setAttribute('aria-pressed', String(on));
      this.classList.toggle('has-sound', on);
      if (!on) {
        if (this.sound) {
          Object.keys(this.holds).forEach(k => this.sound.loop(k, false));
          this.sound.texture(0);
          this.sound.silence();
        }
        this.sound = null;
        return;
      }
      if (!this.soundEngine) {
        let urls = null;
        try { urls = JSON.parse(this.dataset.font || 'null'); } catch (e) { urls = null; }
        this.soundEngine = urls ? new SaberFont(urls) : new SaberSound();
      }
      const eng = this.soundEngine;
      eng.ensure();
      eng.wake();
      const start = engine => {
        if (this.soundBtn.getAttribute('aria-pressed') !== 'true') return;
        this.sound = engine;
        if (this.on) {
          // The blade is already lit: ignite it again so the ignition sound matches what's on screen
          Object.keys(this.holds).forEach(k => this.hold(k, false));
          this.level = 0;
          this.ignite();
        }
      };
      if (eng instanceof SaberFont && !eng.ready) {
        this.soundBtn.classList.add('is-loading');
        eng.load().then(() => {
          this.soundBtn.classList.remove('is-loading');
          if (eng.failed) {
            // font files unavailable: fall back to the synthesized hum
            this.soundEngine = new SaberSound();
            this.soundEngine.ensure();
            this.soundEngine.wake();
            start(this.soundEngine);
          } else {
            start(eng);
            this.status('Sound font · AllySabers');
          }
        });
      } else {
        start(eng);
      }
    }

    /* ---- pointer ---- */

    local(e) {
      const r = this.stage.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    }

    onMove(e) {
      const p = this.local(e);
      if (e.pointerType === 'mouse' || !this.touch) {
        this.aim = Math.atan2(p.y - this.pivot.y, Math.max(70, p.x - this.pivot.x));
        this.hintSeen = this.hintSeen || (this.hint(''), true);
      } else if (this.drag) {
        const dx = p.x - this.drag.x;
        if (Math.abs(dx) > 6) this.drag.moved = true;
        this.aim = clamp(this.drag.a + dx * 0.012, -1.2, 0.9);
      }
    }

    onDown(e) {
      const p = this.local(e);
      this.drag = { x: p.x, y: p.y, a: this.aim != null ? this.aim : this.ang, moved: false };
    }

    onUp(e) {
      const p = this.local(e);
      const d = this.drag;
      this.drag = null;
      if (this.touch) setTimeout(() => { if (!this.drag) this.aim = null; }, 1400);
      if (d && d.moved) return;
      // Did the tap land on the blade?
      if (!this.on || this.level < 0.95) return;
      const dx = p.x - this.pivot.x;
      const dy = p.y - this.pivot.y;
      const c = Math.cos(-this.ang);
      const s = Math.sin(-this.ang);
      const lx = dx * c - dy * s;
      const ly = dx * s + dy * c;
      const t = (lx - this.x0) / this.len;
      if (t > 0.02 && t < 1.04 && Math.abs(ly) < Math.max(22, this.bw * 4)) this.trigger('clash', clamp(t, 0.05, 0.98));
    }

    /* ---- loop ---- */

    wake() {
      if (!this.visible || document.hidden || this.hidden || this.raf) return;
      this.last = performance.now();
      this.raf = requestAnimationFrame(this.frame);
      if (this.sound) this.sound.wake();
      // First time the lab is seen: ignite on its own
      if (!this.autoIgnited && this.shown !== false && !this.hidden) {
        this.autoIgnited = true;
        if (!this.on) setTimeout(() => this.ignite(), 250);
      }
    }

    sleep() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
      if (this.sound) this.sound.silence();
    }

    resize() {
      const r = this.stage.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const W = r.width;
      const H = r.height;
      this.W = W;
      this.H = H;
      this.dpr = dpr;
      this.canvas.width = Math.round(W * dpr);
      this.canvas.height = Math.round(H * dpr);
      const narrow = W < 560;
      this.narrow = narrow;
      this.N = narrow ? 72 : 108;
      this.px = new Float32Array(this.N * 4);
      this.widths = new Float32Array(this.N).fill(1);
      const hl = narrow ? clamp(W * 0.36, 110, 170) : clamp(W * 0.22, 150, 250);
      this.hl = hl;
      this.hr = hl * 0.062;
      this.bw = this.hr * 1.12;
      this.pivot = narrow ? { x: W * 0.17, y: H * 0.7 } : { x: W * 0.16, y: H * 0.5 };
      this.base = narrow ? -0.52 : -0.06;
      this.x0 = hl * 0.64; // emitter mouth, from the pivot
      const ca = Math.cos(Math.abs(this.base));
      const sa = Math.sin(Math.abs(this.base)) || 0.001;
      const room = Math.min((W * 0.95 - this.pivot.x) / ca, (this.pivot.y - H * 0.06) / sa) - this.x0;
      this.len = Math.max(120, Math.min(room, narrow ? W : 780, W * 0.64));
      this.floorY = H * (narrow ? 0.93 : 0.9);

      // Low-resolution light buffers: drawing the blade small and scaling it up gives a cheap, smooth bloom
      const mk = (c, k) => {
        c = c || document.createElement('canvas');
        c.width = Math.max(8, Math.ceil(W / k));
        c.height = Math.max(8, Math.ceil(H / k));
        return c;
      };
      this.glow = mk(this.glow, 4);
      this.halo = mk(this.halo, 12);
      this.floorBuf = mk(this.floorBuf, 2);
      this.buildBackdrop();
      this.buildHilt();
      if (this.scopeCanvas) {
        const sc = this.scopeCanvas.getBoundingClientRect();
        this.scopeCanvas.width = Math.max(40, Math.round(sc.width * dpr));
        this.scopeCanvas.height = Math.max(10, Math.round(sc.height * dpr));
      }
      if (this.stripCanvas) {
        const sr = this.stripCanvas.getBoundingClientRect();
        this.stripCanvas.width = Math.max(40, Math.round(sr.width * dpr));
        this.stripCanvas.height = Math.max(4, Math.round(sr.height * dpr));
      }
      if (!this.haze || this.haze.W !== Math.round(W)) {
        this.haze = Array.from({ length: narrow ? 34 : 60 }, () => ({ x: Math.random() * W, y: Math.random() * H * 0.9, r: 0.5 + Math.random() * 1.4, v: 3 + Math.random() * 9, p: Math.random() * 6.28 }));
        this.haze.W = Math.round(W);
      }
      if (!this.raf) this.draw(0);
    }

    // Static backdrop: chamber gradient, a perspective floor grid (kept as a mask so the blade can light it)
    buildBackdrop() {
      const { W, H, dpr } = this;
      const bg = (this.bg = this.bg || document.createElement('canvas'));
      bg.width = Math.round(W * dpr);
      bg.height = Math.round(H * dpr);
      const g = bg.getContext('2d');
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      const grad = g.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, '#07090d');
      grad.addColorStop(0.7, '#05070a');
      grad.addColorStop(1, '#020304');
      g.fillStyle = grad;
      g.fillRect(0, 0, W, H);
      // back wall: fine horizontal scan lines
      g.fillStyle = 'rgba(255,255,255,.018)';
      for (let y = 0; y < this.floorY; y += 4) g.fillRect(0, y, W, 1);

      // floor grid (mask)
      const grid = (this.grid = this.grid || document.createElement('canvas'));
      grid.width = this.floorBuf.width;
      grid.height = this.floorBuf.height;
      const m = grid.getContext('2d');
      const k = grid.width / W;
      m.setTransform(k, 0, 0, k, 0, 0);
      m.clearRect(0, 0, W, H);
      m.strokeStyle = '#fff';
      m.lineWidth = 1 / k;
      const fy = this.floorY - H * 0.16;
      const vx = W * 0.5;
      for (let i = -14; i <= 14; i++) {
        m.beginPath();
        m.moveTo(vx + i * W * 0.02, fy);
        m.lineTo(vx + i * W * 0.16, H);
        m.stroke();
      }
      for (let j = 0; j < 9; j++) {
        const y = fy + (H - fy) * Math.pow(j / 8, 1.8);
        m.globalAlpha = 0.35 + 0.65 * (j / 8);
        m.beginPath();
        m.moveTo(0, y);
        m.lineTo(W, y);
        m.stroke();
      }
      m.globalAlpha = 1;
      // faint base grid always visible
      g.globalAlpha = 0.07;
      g.drawImage(grid, 0, 0, W, H);
      g.globalAlpha = 1;
      // vignette
      const v = g.createRadialGradient(W * 0.5, H * 0.45, Math.min(W, H) * 0.2, W * 0.5, H * 0.5, Math.max(W, H) * 0.75);
      v.addColorStop(0, 'rgba(0,0,0,0)');
      v.addColorStop(1, 'rgba(0,0,0,.65)');
      g.fillStyle = v;
      g.fillRect(0, 0, W, H);
    }

    // The hilt is drawn once into a sprite (it only changes on resize) and rotated with the blade
    buildHilt() {
      const { hl, hr, dpr } = this;
      const pad = hr * 2.4;
      const c = (this.hiltSprite = this.hiltSprite || document.createElement('canvas'));
      c.width = Math.ceil((hl + pad * 2) * dpr);
      c.height = Math.ceil((hr * 2 + pad * 2) * dpr);
      const g = c.getContext('2d');
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, c.width, c.height);
      g.translate(pad, hr + pad);
      this.hiltOffset = { x: -hl * 0.36 - pad, y: -hr - pad };

      const metal = (R, tone = 1) => {
        const gr = g.createLinearGradient(0, -R, 0, R);
        gr.addColorStop(0, `rgba(${40 * tone},${44 * tone},${52 * tone},1)`);
        gr.addColorStop(0.16, '#c9cfd8');
        gr.addColorStop(0.3, '#fbfcfe');
        gr.addColorStop(0.44, '#a3abb6');
        gr.addColorStop(0.7, '#4a515b');
        gr.addColorStop(1, '#101216');
        return gr;
      };
      const dark = R => {
        const gr = g.createLinearGradient(0, -R, 0, R);
        gr.addColorStop(0, '#15181c');
        gr.addColorStop(0.24, '#4d535c');
        gr.addColorStop(0.36, '#262a30');
        gr.addColorStop(1, '#060708');
        return gr;
      };
      const part = (x0, x1, R, fill) => {
        g.fillStyle = fill;
        g.fillRect(x0 * hl, -R, (x1 - x0) * hl, R * 2);
      };

      // pommel (rounded cap)
      g.fillStyle = dark(hr * 1.06);
      g.beginPath();
      g.moveTo(0.075 * hl, -hr * 1.06);
      g.lineTo(0.02 * hl, -hr * 1.06);
      g.quadraticCurveTo(0, -hr * 1.06, 0, -hr * 0.6);
      g.lineTo(0, hr * 0.6);
      g.quadraticCurveTo(0, hr * 1.06, 0.02 * hl, hr * 1.06);
      g.lineTo(0.075 * hl, hr * 1.06);
      g.closePath();
      g.fill();
      part(0.075, 0.105, hr * 1.12, metal(hr * 1.12));
      part(0.105, 0.115, hr * 0.9, '#0b0c0e');
      // grip: black rubber with ribs
      part(0.115, 0.5, hr * 0.97, dark(hr * 0.97));
      for (let i = 0; i < 9; i++) {
        const x = (0.135 + i * 0.041) * hl;
        g.fillStyle = 'rgba(0,0,0,.65)';
        g.fillRect(x, -hr * 0.97, hl * 0.012, hr * 1.94);
        g.fillStyle = 'rgba(255,255,255,.08)';
        g.fillRect(x + hl * 0.012, -hr * 0.97, hl * 0.004, hr * 1.94);
      }
      part(0.5, 0.535, hr * 1.06, metal(hr * 1.06));
      // control section with activation box
      part(0.535, 0.72, hr, metal(hr, 0.9));
      g.fillStyle = dark(hr * 1.3);
      g.fillRect(0.575 * hl, -hr * 1.32, 0.1 * hl, hr * 0.5);
      g.fillStyle = metal(hr * 0.3);
      g.fillRect(0.6 * hl, -hr * 1.5, 0.05 * hl, hr * 0.22);
      this.ledAt = { x: 0.625 * hl, y: -hr * 1.07 };
      // machined grooves on the control section
      g.fillStyle = 'rgba(0,0,0,.55)';
      [0.55, 0.69, 0.705].forEach(x => g.fillRect(x * hl, -hr, hl * 0.006, hr * 2));
      // neck with vents
      part(0.72, 0.8, hr * 0.84, metal(hr * 0.84, 0.8));
      g.fillStyle = '#07080a';
      [0.735, 0.755, 0.775].forEach(x => g.fillRect(x * hl, -hr * 0.62, hl * 0.009, hr * 1.24));
      // emitter: flared shroud
      g.fillStyle = metal(hr * 1.2);
      g.beginPath();
      g.moveTo(0.8 * hl, -hr * 0.95);
      g.lineTo(0.985 * hl, -hr * 1.2);
      g.lineTo(0.985 * hl, hr * 1.2);
      g.lineTo(0.8 * hl, hr * 0.95);
      g.closePath();
      g.fill();
      g.fillStyle = 'rgba(0,0,0,.7)';
      g.fillRect(0.85 * hl, -hr * 0.34, 0.08 * hl, hr * 0.2);
      g.fillRect(0.85 * hl, hr * 0.14, 0.08 * hl, hr * 0.2);
      part(0.985, 1, hr * 1.24, '#0a0b0d');
      // soft edge shading
      g.globalCompositeOperation = 'source-atop';
      const edge = g.createLinearGradient(0, 0, hl, 0);
      edge.addColorStop(0, 'rgba(0,0,0,.35)');
      edge.addColorStop(0.2, 'rgba(0,0,0,0)');
      edge.addColorStop(1, 'rgba(0,0,0,.1)');
      g.fillStyle = edge;
      g.fillRect(-pad, -hr * 2, hl + pad * 2, hr * 4);
      g.globalCompositeOperation = 'source-over';
    }

    frame(now) {
      const raw = (now - this.last) / 1000;
      const dt = Math.min(0.05, raw);
      this.last = now;
      // Adaptive quality: if frames run long on this device, switch to the lighter bloom
      if (!this.lowQ && raw < 0.2) {
        this.slowAvg = (this.slowAvg || 0.016) * 0.95 + raw * 0.05;
        this.frames = (this.frames || 0) + 1;
        if (this.frames > 45 && this.slowAvg > 0.028) this.lowQ = true;
      }
      this.draw(dt);
      this.raf = this.visible && !document.hidden && !this.hidden ? requestAnimationFrame(this.frame) : null;
    }

    /* ---- simulation ---- */

    step(dt) {
      this.time += dt;
      const t = this.time;
      // colour eases toward the chosen swatch
      for (let i = 0; i < 3; i++) this.rgb[i] += (this.target[i] - this.rgb[i]) * Math.min(1, dt * 9);

      // ignition
      const caps = LAB_CORES[this.core];
      const dur = this.motion > 0 ? (caps.ignite === 'fade' ? 0.28 : caps.ignite === 'spark' ? 0.62 : 0.42) : caps.ignite === 'fade' ? 0.3 : 0.5;
      const before = this.level;
      if (this.motion) this.level = clamp(this.level + (this.motion * dt) / dur, 0, 1);
      if (this.motion > 0 && before < 1 && this.level === 1) this.flash = 1; // full extension
      if ((this.motion > 0 && this.level >= 1) || (this.motion < 0 && this.level <= 0)) this.motion = 0;
      this.flash = Math.max(0, this.flash - dt * 4);

      // swing physics: a damped spring toward the pointer (or a gentle idle sway)
      const idle = reduceMotion.matches ? 0 : Math.sin(t * 0.8) * 0.025 + Math.sin(t * 1.9) * 0.008;
      const goal = this.aim != null ? clamp(this.aim, -1.25, 0.95) : this.base + idle;
      const acc = (goal - this.ang) * 70 - this.vel * 12;
      this.vel += acc * dt;
      this.ang += this.vel * dt;
      const speed = Math.min(1, Math.abs(this.vel) / 7);
      this.speed = speed;
      if (this.on && this.sound) {
        if (this.sound.continuous) this.sound.swing(this.level > 0.9 ? speed : 0);
        else if (speed > 0.16) this.sound.swing(speed);
      }
      this.trail.unshift(this.ang);
      this.trail.length = Math.min(this.trail.length, 7);

      this.events = this.events.filter(e => t - e.t0 < 1);
      this.shake = Math.max(0, (this.shake || 0) - dt * 2.8);

      // continuous sparks for lockup / drag
      if (this.level > 0.9 && !reduceMotion.matches) {
        if (this.holds.lockup && caps.local) this.spray(this.holds.lockup.pos, 2, 0.7);
        if (this.holds.drag) this.spray(1, 3, 0.8, true);
      }

      // sparks
      const g = 900;
      this.sparks = this.sparks.filter(s => (s.life -= dt) > 0);
      this.sparks.forEach(s => {
        s.vy += g * dt;
        s.x += s.vx * dt;
        s.y += s.vy * dt;
        if (s.y > this.floorY) { s.y = this.floorY; s.vy *= -0.35; s.vx *= 0.6; }
      });

      // with the sound font on, the blade breathes with the hum (like an audio-flicker style)
      const an = this.sound && this.sound.analyser;
      if (an && this.on) {
        const d = (this.levelData = this.levelData && this.levelData.length === an.fftSize ? this.levelData : new Uint8Array(an.fftSize));
        an.getByteTimeDomainData(d);
        let sum = 0;
        for (let i = 0; i < d.length; i += 4) { const v = (d[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / (d.length / 4));
        this.audio = (this.audio || 0) * 0.6 + rms * 0.4;
      } else {
        this.audio = 0;
      }

      this.computePixels(t);
    }

    // Colour and brightness of every pixel along the blade
    computePixels(t) {
      const caps = LAB_CORES[this.core];
      const N = this.N;
      const px = this.px;
      const [br, bg, bb] = this.rgb;
      if (this.bladeStyle === 'custom') {
        // second colour: the blade colour rotated 55° round the colour wheel
        const mx = Math.max(br, bg, bb), mn = Math.min(br, bg, bb), d = mx - mn || 1;
        let h = mx === br ? ((bg - bb) / d) % 6 : mx === bg ? (bb - br) / d + 2 : (br - bg) / d + 4;
        this.second = hueRgb((h * 60 + 415) % 360);
      }
      const lvl = this.level;
      const ease = 1 - Math.pow(1 - lvl, 3);
      let front = 1;
      let fadeK = 1;
      if (caps.ignite === 'fade') fadeK = ease * (1 + 0.5 * this.flash);
      else front = this.motion < 0 ? lvl : ease;
      const lock = this.holds.lockup;
      const drag = this.holds.drag;
      const flick = Math.random();
      let sumR = 0, sumG = 0, sumB = 0, sumI = 0;

      for (let i = 0; i < N; i++) {
        const x = i / (N - 1);
        let r = br, g = bg, b = bb;
        let I = 1;
        let w = 1; // width factor

        if (caps.local) {
          if (this.bladeStyle === 'unstable') {
            const n1 = noise(x * 9 - t * 15);
            const n2 = noise(x * 27 + t * 37);
            I = 0.72 + 0.45 * n1 + (n2 > 0.82 ? 0.5 : 0);
            w = 0.8 + 0.55 * n2;
            if (n2 > 0.86) { r += (255 - r) * 0.6; g += (255 - g) * 0.6; b += (255 - b) * 0.6; }
          } else if (this.bladeStyle === 'fire') {
            const heat = clamp(noise(x * 6 - t * 4.5) * (1.05 - x * 0.35) + noise(x * 17 - t * 9) * 0.45 - 0.2, 0, 1);
            r += (255 - r) * heat * 0.85;
            g += (180 - g) * heat * 0.7;
            b += (60 - b) * heat * 0.8;
            I = 0.78 + 0.5 * heat;
            w = 0.9 + 0.3 * heat;
          } else if (this.bladeStyle === 'rainbow') {
            [r, g, b] = hueRgb((x * 320 + t * 150) % 360);
            I = 1.02;
          } else if (this.bladeStyle === 'custom') {
            // example custom style: a second colour sweeps up the blade with white-hot pulses
            const [cr, cg, cb] = this.second || [br, bg, bb];
            const sweep = 0.5 + 0.5 * Math.sin(x * 5 - t * 2.4);
            const pulse = Math.pow(Math.max(0, Math.sin(x * 22 - t * 11)), 12);
            r = br + (cr - br) * sweep;
            g = bg + (cg - bg) * sweep;
            b = bb + (cb - bb) * sweep;
            r += (255 - r) * pulse * 0.8; g += (255 - g) * pulse * 0.8; b += (255 - b) * pulse * 0.8;
            I = 0.9 + 0.55 * pulse;
          } else {
            I = 0.97 + 0.03 * noise(x * 5 - t * 2.5) - 0.02 * noise(t * 9);
          }
          // ignition mask: hilt-to-tip scroll with a bright leading edge
          if (x > front) { I = 0; }
          else if (front < 1) {
            const edge = clamp(1 - (front - x) / 0.07, 0, 1);
            const k = caps.ignite === 'spark' ? 0.95 : 0.55;
            r += (255 - r) * edge * k; g += (255 - g) * edge * k; b += (255 - b) * edge * k;
            I *= 1 + edge * (caps.ignite === 'spark' ? 1 : 0.4);
          }
          if (this.flash && caps.ignite === 'spark') { const f = this.flash * 0.6; r += (255 - r) * f; g += (255 - g) * f; b += (255 - b) * f; }
        } else {
          // Base-Lit: one LED in the hilt lights a diffusing tube. Slightly brighter at the base, all at once.
          I = (1.06 - 0.2 * x) * fadeK * (0.985 + 0.015 * noise(t * 7));
        }

        // effects
        for (const e of this.events) {
          if (e.type === 'force') {
            const a = (t - e.t0) / 0.95;
            if (a >= 1) continue;
            const k = caps.local ? Math.exp(-Math.pow((x - a * 1.25) / 0.09, 2)) * (1 - a) * 0.9 + 0.3 * Math.sin(a * Math.PI) : 0.55 * Math.sin(a * Math.PI);
            r += (255 - r) * k * 0.55; g += (255 - g) * k * 0.55; b += (255 - b) * k * 0.55;
            I += k * 0.8;
            w *= 1 + k * 0.35;
            continue;
          }
          const a = (t - e.t0) / (e.type === 'clash' ? 0.32 : 0.55);
          if (a >= 1) continue;
          let k;
          if (!caps.local) k = (1 - a) * (1 - a) * (e.type === 'clash' ? 0.95 : 0.55);
          else if (e.type === 'clash') k = Math.exp(-Math.pow((x - e.pos) / 0.075, 2)) * (1 - a) + 0.35 * Math.pow(1 - a, 3);
          else {
            const d = Math.abs(x - e.pos);
            k = (Math.exp(-Math.pow(d / 0.035, 2)) * Math.pow(1 - a, 2) + Math.exp(-Math.pow((d - a * 0.14) / 0.022, 2)) * (1 - a) * 0.8);
          }
          r += (255 - r) * Math.min(1, k); g += (255 - g) * Math.min(1, k); b += (255 - b) * Math.min(1, k);
          I += k * 0.9;
        }
        if (lock) {
          if (caps.local) {
            const k = Math.exp(-Math.pow((x - lock.pos) / 0.06, 2)) * (0.55 + 0.45 * flick);
            r += (255 - r) * k; g += (235 - g) * k; b += (160 - b) * k;
            I += k * 0.9 + 0.08 * flick;
          } else {
            const k = 0.25 + 0.45 * flick;
            r += (255 - r) * k * 0.5; g += (255 - g) * k * 0.5; b += (255 - b) * k * 0.5;
            I *= 0.8 + 0.5 * flick;
          }
        }
        if (drag && caps.local) {
          const k = clamp((x - 0.86) / 0.14, 0, 1) * (0.6 + 0.4 * Math.random());
          r += (255 - r) * k; g += (150 - g) * k; b += (40 - b) * k;
          I += k * 0.8;
        }

        if (this.audio) I *= 0.9 + Math.min(0.35, this.audio * 1.6) * 0.55;
        const o = i * 4;
        px[o] = r; px[o + 1] = g; px[o + 2] = b; px[o + 3] = I;
        this.widths[i] = w;
        if (I > 0) { sumR += r * I; sumG += g * I; sumB += b * I; sumI += I; }
      }
      const n = Math.max(1, sumI);
      this.avg = [sumR / n, sumG / n, sumB / n];
      this.lit = sumI / N; // 0..~1.2
    }

    spray(pos, count, force, fromTip) {
      if (reduceMotion.matches || !this.pivot) return;
      const c = Math.cos(this.ang);
      const s = Math.sin(this.ang);
      const d = this.x0 + this.len * pos;
      const x = this.pivot.x + c * d;
      const y = this.pivot.y + s * d;
      for (let i = 0; i < count; i++) {
        const a = fromTip ? Math.PI / 2 + (Math.random() - 0.5) * 2.2 : this.ang + (Math.random() < 0.5 ? -1 : 1) * Math.PI / 2 + (Math.random() - 0.5) * 1.6;
        const v = (140 + Math.random() * 380) * force;
        this.sparks.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 120 * force, life: 0.25 + Math.random() * 0.55, max: 0.8 });
      }
      if (this.sparks.length > 260) this.sparks.splice(0, this.sparks.length - 260);
    }

    /* ---- rendering ---- */

    // Stroke the blade as runs of similar pixels (fewer draw calls), in world coordinates
    strokeBlade(g, width, alphaK, whiten, minI, overlap = 0) {
      const { N, px, x0, len } = this;
      let i = 0;
      g.lineCap = 'butt';
      while (i < N) {
        const o = i * 4;
        const I = px[o + 3];
        if (I <= minI) { i++; continue; }
        const q = v => Math.round(v / 24);
        const key = q(px[o]) * 961 + q(px[o + 1]) * 31 + q(px[o + 2]);
        const ki = Math.round(I * 10);
        const wi = this.widths ? Math.round(this.widths[i] * 8) : 8;
        let j = i + 1;
        while (j < N) {
          const p = j * 4;
          if (px[p + 3] <= minI) break;
          if (q(px[p]) * 961 + q(px[p + 1]) * 31 + q(px[p + 2]) !== key || Math.round(px[p + 3] * 10) !== ki || (this.widths && Math.round(this.widths[j] * 8) !== wi)) break;
          j++;
        }
        const r = px[o] + (255 - px[o]) * whiten;
        const gg = px[o + 1] + (255 - px[o + 1]) * whiten;
        const b = px[o + 2] + (255 - px[o + 2]) * whiten;
        g.strokeStyle = `rgba(${r | 0},${gg | 0},${b | 0},${Math.min(1, I * alphaK)})`;
        g.lineWidth = width * (wi / 8);
        g.beginPath();
        g.moveTo(x0 + len * (i / (N - 1)) - (i ? overlap : 0), 0);
        g.lineTo(x0 + len * (Math.min(j, N - 1) / (N - 1)) + overlap, 0);
        g.stroke();
        i = j;
      }
    }

    tipIndex() {
      const { N, px } = this;
      for (let i = N - 1; i >= 0; i--) if (px[i * 4 + 3] > 0.02) return i;
      return -1;
    }

    draw(dt) {
      if (!this.W || !this.ctx) return;
      this.step(dt);
      const { ctx, W, H, dpr, pivot } = this;
      const on = this.lit > 0.01;
      const [ar, ag, ab] = this.avg || this.rgb;
      const light = Math.min(1.2, this.lit || 0);

      const shake = this.shake && !reduceMotion.matches ? this.shake * this.shake * 5 : 0;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(this.bg, 0, 0, W, H);
      if (shake) ctx.translate((Math.random() - 0.5) * shake, (Math.random() - 0.5) * shake);

      const c = Math.cos(this.ang);
      const s = Math.sin(this.ang);
      const mid = this.x0 + this.len * 0.5;
      const mx = pivot.x + c * mid;
      const my = pivot.y + s * mid;

      if (on) {
        ctx.globalCompositeOperation = 'lighter';
        // light spill into the chamber
        const spill = ctx.createRadialGradient(mx, my, 0, mx, my, this.len * 0.95);
        spill.addColorStop(0, `rgba(${ar | 0},${ag | 0},${ab | 0},${0.2 * light})`);
        spill.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = spill;
        ctx.fillRect(0, 0, W, H);

        // floor: a pool of blade light, visible on the grid lines
        const fb = this.floorBuf;
        const f = fb.getContext('2d');
        const k = fb.width / W;
        f.setTransform(k, 0, 0, k, 0, 0);
        f.globalCompositeOperation = 'source-over';
        f.clearRect(0, 0, W, H);
        const height = Math.max(20, this.floorY - my);
        const strength = Math.min(1, 140 / height) * light;
        const pool = f.createRadialGradient(mx, this.floorY, 0, mx, this.floorY, this.len * 0.75);
        pool.addColorStop(0, `rgba(${ar | 0},${ag | 0},${ab | 0},${0.9 * strength})`);
        pool.addColorStop(1, 'rgba(0,0,0,0)');
        f.fillStyle = pool;
        f.fillRect(0, this.floorY - H * 0.2, W, H);
        f.globalCompositeOperation = 'destination-in';
        f.setTransform(1, 0, 0, 1, 0, 0);
        f.drawImage(this.grid, 0, 0);
        ctx.drawImage(fb, 0, 0, W, H);
        // soft reflection glow on the floor
        const refl = ctx.createRadialGradient(mx, this.floorY, 0, mx, this.floorY, this.len * 0.5);
        refl.addColorStop(0, `rgba(${ar | 0},${ag | 0},${ab | 0},${0.16 * strength})`);
        refl.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = refl;
        ctx.fillRect(0, this.floorY - H * 0.12, W, H);
      }

      // haze particles, lit by the blade
      if (this.haze) {
        ctx.globalCompositeOperation = 'lighter';
        const ex = pivot.x + c * this.x0, ey = pivot.y + s * this.x0;
        const tx = pivot.x + c * (this.x0 + this.len), ty = pivot.y + s * (this.x0 + this.len);
        const vx = tx - ex, vy = ty - ey, vv = vx * vx + vy * vy;
        for (const p of this.haze) {
          if (!reduceMotion.matches) {
            p.x += (p.v + (p.ix || 0)) * dt;
            p.y += Math.sin(this.time * 0.6 + p.p) * dt * 3 + (p.iy || 0) * dt;
            if (p.ix) { p.ix *= Math.max(0, 1 - dt * 2.5); p.iy *= Math.max(0, 1 - dt * 2.5); }
            if (p.y < -4) p.y = this.H + 2; else if (p.y > this.H + 4) p.y = -2;
            if (p.x < -6) p.x = this.W + 4;
          }
          if (p.x > W + 4) p.x = -4;
          const u = clamp(((p.x - ex) * vx + (p.y - ey) * vy) / vv, 0, 1);
          const d = Math.hypot(p.x - (ex + vx * u), p.y - (ey + vy * u));
          const a = 0.05 + (on ? 0.75 * light * Math.exp(-d / 55) : 0);
          ctx.fillStyle = `rgba(${(ar + 255) / 2 | 0},${(ag + 255) / 2 | 0},${(ab + 255) / 2 | 0},${a * (0.6 + 0.4 * Math.sin(this.time * 2 + p.p))})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, 6.283);
          ctx.fill();
        }
      }

      if (on) {
        // bloom buffers: the blade (and its swing trail) drawn small, then scaled up
        const paint = (buf, scale, width, alpha, trail, blur) => {
          const b = buf.getContext('2d');
          b.setTransform(1, 0, 0, 1, 0, 0);
          b.filter = 'none';
          b.clearRect(0, 0, buf.width, buf.height);
          b.setTransform(scale, 0, 0, scale, 0, 0);
          b.globalCompositeOperation = 'lighter';
          const blurOk = LAB_BLUR && !this.lowQ;
          if (blurOk) b.filter = `blur(${blur}px)`;
          if (trail && this.speed > 0.2 && !reduceMotion.matches && blurOk) {
            this.trail.forEach((a, n) => {
              if (!n) return;
              b.save();
              b.translate(pivot.x, pivot.y);
              b.rotate(a);
              b.globalAlpha = (1 - n / this.trail.length) * 0.45 * this.speed;
              this.strokeBlade(b, width, alpha, 0, 0.02);
              b.restore();
            });
            b.globalAlpha = 1;
          }
          b.save();
          b.translate(pivot.x, pivot.y);
          b.rotate(this.ang);
          if (blurOk) this.strokeBlade(b, width, alpha, 0, 0.02);
          else {
            // no canvas blur here: soft radial dots along the blade give the same falloff
            const R = width * 1.8;
            const k = Math.min(1, ((this.len * 2) / (this.N - 1)) / (R * 0.9)); // overlap compensation
            for (let i = 0; i < this.N; i += 2) {
              const o = i * 4;
              const I = this.px[o + 3];
              if (I <= 0.02) continue;
              const x = this.x0 + this.len * (i / (this.N - 1));
              const gr = b.createRadialGradient(x, 0, 0, x, 0, R);
              gr.addColorStop(0, `rgba(${this.px[o] | 0},${this.px[o + 1] | 0},${this.px[o + 2] | 0},${Math.min(1, I * alpha * k * 1.1)})`);
              gr.addColorStop(1, 'rgba(0,0,0,0)');
              b.fillStyle = gr;
              b.fillRect(x - R, -R, R * 2, R * 2);
            }
          }
          // tip cap
          const ti = this.tipIndex();
          if (ti > 0) {
            const o = ti * 4;
            b.fillStyle = `rgba(${this.px[o] | 0},${this.px[o + 1] | 0},${this.px[o + 2] | 0},${Math.min(1, this.px[o + 3] * alpha)})`;
            b.beginPath();
            b.arc(this.x0 + this.len * (ti / (this.N - 1)), 0, width / 2, 0, 6.283);
            b.fill();
          }
          b.restore();
        };
        paint(this.halo, this.halo.width / W, this.bw * 2.2, 0.85, true, 3);
        paint(this.glow, this.glow.width / W, this.bw * 1.1, 0.9, true, 2.5);
        ctx.globalCompositeOperation = 'lighter';
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(this.halo, 0, 0, W, H);
        ctx.drawImage(this.halo, 0, 0, W, H);
        ctx.drawImage(this.glow, 0, 0, W, H);
      }

      // hilt
      ctx.globalCompositeOperation = 'source-over';
      ctx.save();
      ctx.translate(pivot.x, pivot.y);
      ctx.rotate(this.ang);
      ctx.drawImage(this.hiltSprite, this.hiltOffset.x, this.hiltOffset.y, this.hiltSprite.width / dpr, this.hiltSprite.height / dpr);
      const hx = -this.hl * 0.36;
      if (on) {
        // blade light catching the metal, strongest at the emitter (masked to the hilt's own shape)
        const hs = this.hiltSprite;
        const rb = (this.rimBuf = this.rimBuf || document.createElement('canvas'));
        if (rb.width !== hs.width || rb.height !== hs.height) { rb.width = hs.width; rb.height = hs.height; }
        const r = rb.getContext('2d');
        r.globalCompositeOperation = 'source-over';
        r.clearRect(0, 0, rb.width, rb.height);
        r.drawImage(hs, 0, 0);
        r.globalCompositeOperation = 'source-in';
        const rim = r.createLinearGradient(rb.width * 0.35, 0, rb.width * 0.95, 0);
        rim.addColorStop(0, 'rgba(0,0,0,0)');
        rim.addColorStop(1, `rgba(${ar | 0},${ag | 0},${ab | 0},1)`);
        r.fillStyle = rim;
        r.fillRect(0, 0, rb.width, rb.height);
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = 0.5 * Math.min(1, light);
        ctx.drawImage(rb, this.hiltOffset.x, this.hiltOffset.y, hs.width / dpr, hs.height / dpr);
        ctx.globalAlpha = 1;
      }
      // activation LED
      ctx.globalCompositeOperation = 'lighter';
      const led = this.ledAt;
      const lx = hx + led.x;
      const ledCol = this.on ? `rgba(${this.rgb[0] | 0},${this.rgb[1] | 0},${this.rgb[2] | 0},1)` : 'rgba(255,60,60,.5)';
      const lg = ctx.createRadialGradient(lx, led.y, 0, lx, led.y, this.hr * 0.9);
      lg.addColorStop(0, ledCol);
      lg.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = lg;
      ctx.fillRect(lx - this.hr, led.y - this.hr, this.hr * 2, this.hr * 2);

      // blade core: coloured sheath, then the white-hot centre
      if (on) {
        this.strokeBlade(ctx, this.bw * 1.02, 0.8, 0.3, 0.02);
        ctx.globalCompositeOperation = 'source-over';
        this.strokeBlade(ctx, this.bw * 0.6, 1.05, 0.93, 0.05, 0.5);
        const ti = this.tipIndex();
        if (ti > 0) {
          const o = ti * 4;
          const cx = this.x0 + this.len * (ti / (this.N - 1));
          ctx.fillStyle = `rgba(${(this.px[o] + 255 * 3) / 4 | 0},${(this.px[o + 1] + 255 * 3) / 4 | 0},${(this.px[o + 2] + 255 * 3) / 4 | 0},${Math.min(1, this.px[o + 3])})`;
          ctx.beginPath();
          ctx.arc(cx, 0, this.bw * 0.52, -Math.PI / 2, Math.PI / 2);
          ctx.fill();
        }
        // emitter glow
        ctx.globalCompositeOperation = 'lighter';
        const eg = ctx.createRadialGradient(this.x0, 0, 0, this.x0, 0, this.hr * 2.4);
        eg.addColorStop(0, `rgba(255,255,255,${0.5 * light})`);
        eg.addColorStop(0.4, `rgba(${ar | 0},${ag | 0},${ab | 0},${0.35 * light})`);
        eg.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = eg;
        ctx.fillRect(this.x0 - this.hr * 2.4, -this.hr * 2.4, this.hr * 4.8, this.hr * 4.8);
      }
      ctx.restore();

      // cinematic lens streak when the blade reaches full length
      if (this.flash > 0 && on && !reduceMotion.matches) {
        const tipD = this.x0 + this.len;
        const tx = pivot.x + c * tipD, ty = pivot.y + s * tipD;
        const fw = W * 0.45 * (0.6 + this.flash * 0.4);
        const st = ctx.createLinearGradient(tx - fw, 0, tx + fw, 0);
        st.addColorStop(0, 'rgba(0,0,0,0)');
        st.addColorStop(0.5, `rgba(${(ar + 510) / 3 | 0},${(ag + 510) / 3 | 0},${(ab + 510) / 3 | 0},${0.7 * this.flash})`);
        st.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.globalCompositeOperation = 'lighter';
        ctx.fillStyle = st;
        ctx.fillRect(tx - fw, ty - 1.5, fw * 2, 3);
        ctx.globalAlpha = 0.35;
        ctx.fillRect(tx - fw * 0.6, ty - 6, fw * 1.2, 12);
        ctx.globalAlpha = 1;
      }

      // force: a shockwave rolls out from the blade
      for (const e of this.events) {
        const a = (this.time - e.t0) / 0.85;
        if (e.type !== 'force' || a >= 1) continue;
        ctx.globalCompositeOperation = 'lighter';
        ctx.save();
        ctx.translate(mx, my);
        ctx.rotate(this.ang);
        ctx.scale(1, 0.55);
        for (let ring = 0; ring < 2; ring++) {
          const ra = Math.max(0, a - ring * 0.18);
          if (!ra) continue;
          ctx.strokeStyle = `rgba(${(ar + 255) / 2 | 0},${(ag + 255) / 2 | 0},${(ab + 255) / 2 | 0},${0.5 * (1 - ra) * (ring ? 0.6 : 1)})`;
          ctx.lineWidth = 1.5 + 7 * (1 - ra);
          ctx.beginPath();
          ctx.ellipse(0, 0, this.len * (0.35 + ra * 0.9), this.len * (0.08 + ra * 0.6), 0, 0, 6.283);
          ctx.stroke();
        }
        ctx.restore();
      }

      // clash flash at the point of contact
      for (const e of this.events) {
        const a = (this.time - e.t0) / 0.16;
        if (e.type !== 'clash' || a >= 1) continue;
        const d = this.x0 + this.len * (LAB_CORES[this.core].local ? e.pos : 0.6);
        const fx = pivot.x + c * d, fy = pivot.y + s * d;
        const rad = (LAB_CORES[this.core].local ? 70 : 140) * (0.6 + a);
        const fl = ctx.createRadialGradient(fx, fy, 0, fx, fy, rad);
        fl.addColorStop(0, `rgba(255,255,255,${0.85 * (1 - a)})`);
        fl.addColorStop(0.3, `rgba(${ar | 0},${ag | 0},${ab | 0},${0.4 * (1 - a)})`);
        fl.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.globalCompositeOperation = 'lighter';
        ctx.fillStyle = fl;
        ctx.fillRect(fx - rad, fy - rad, rad * 2, rad * 2);
      }

      // sparks
      if (this.sparks.length) {
        ctx.globalCompositeOperation = 'lighter';
        ctx.lineCap = 'round';
        for (const sp of this.sparks) {
          const k = Math.min(1, sp.life / 0.5);
          ctx.strokeStyle = `rgba(255,${(170 + 85 * k) | 0},${(90 + 150 * k) | 0},${k})`;
          ctx.lineWidth = 1.8;
          ctx.beginPath();
          ctx.moveTo(sp.x, sp.y);
          ctx.lineTo(sp.x - sp.vx * 0.018, sp.y - sp.vy * 0.018);
          ctx.stroke();
        }
      }
      ctx.globalCompositeOperation = 'source-over';

      this.drawHud();
    }

    drawHud() {
      // pixel map: what each LED is doing right now (Base-Lit: one source in the hilt)
      const sc = this.stripCanvas;
      if (sc && sc.width) {
        const g = sc.getContext('2d');
        const w = sc.width, h = sc.height;
        g.clearRect(0, 0, w, h);
        const caps = LAB_CORES[this.core];
        if (caps.local) {
          const n = 48;
          const cw = w / n;
          for (let i = 0; i < n; i++) {
            const o = Math.round((i / (n - 1)) * (this.N - 1)) * 4;
            const I = Math.min(1, this.px[o + 3]);
            g.fillStyle = I > 0.02 ? `rgba(${this.px[o] | 0},${this.px[o + 1] | 0},${this.px[o + 2] | 0},${0.25 + 0.75 * I})` : 'rgba(255,255,255,.08)';
            g.fillRect(i * cw + 1, 0, Math.max(1, cw - 2), h);
          }
        } else {
          const I = Math.min(1, this.px[3] || 0);
          const [r, gg, b] = [this.px[0] | 0, this.px[1] | 0, this.px[2] | 0];
          const grad = g.createLinearGradient(0, 0, w, 0);
          grad.addColorStop(0, `rgba(${r},${gg},${b},${I})`);
          grad.addColorStop(1, `rgba(${r},${gg},${b},${I * 0.25})`);
          g.fillStyle = 'rgba(255,255,255,.08)';
          g.fillRect(0, 0, w, h);
          g.fillStyle = grad;
          g.fillRect(h * 1.6, 0, w - h * 1.6, h);
          g.fillStyle = I > 0.02 ? '#fff' : 'rgba(255,255,255,.3)';
          g.fillRect(0, 0, h * 1.2, h);
        }
      }
      // audio scope: the sound font's live waveform
      const scope = this.scopeCanvas;
      const an = this.sound && this.sound.analyser;
      if (scope && scope.width) {
        const g = scope.getContext('2d');
        g.clearRect(0, 0, scope.width, scope.height);
        if (an) {
          const data = (this.scopeData = this.scopeData && this.scopeData.length === an.fftSize ? this.scopeData : new Uint8Array(an.fftSize));
          an.getByteTimeDomainData(data);
          const [r, gg, b] = (this.avg || this.rgb).map(v => v | 0);
          g.lineWidth = Math.max(1, scope.height / 16);
          g.strokeStyle = `rgb(${(r + 255) / 2 | 0},${(gg + 255) / 2 | 0},${(b + 255) / 2 | 0})`;
          g.shadowColor = `rgb(${r},${gg},${b})`;
          g.shadowBlur = 6;
          g.beginPath();
          const step = data.length / scope.width;
          for (let x = 0; x < scope.width; x++) {
            const v = data[Math.floor(x * step)] / 128 - 1;
            const y = scope.height / 2 + v * scope.height * 0.9;
            x ? g.lineTo(x, y) : g.moveTo(x, y);
          }
          g.stroke();
          g.shadowBlur = 0;
        }
      }

      // swing meter
      const lit = Math.round((this.speed || 0) * this.swingBars.length);
      if (lit !== this.swingLit) {
        this.swingLit = lit;
        this.swingBars.forEach((b, i) => b.classList.toggle('on', i < lit));
      }
    }
  }

  if (!customElements.get('ig-blade-lab')) customElements.define('ig-blade-lab', IgBladeLab);
  if (!customElements.get('ig-hero')) customElements.define('ig-hero', IgHero);
  if (!customElements.get('ig-core-lab')) customElements.define('ig-core-lab', IgCoreLab);
  if (!customElements.get('ig-rail')) customElements.define('ig-rail', IgRail);
  if (!customElements.get('ig-finder')) customElements.define('ig-finder', IgFinder);
  if (!customElements.get('ig-cores')) customElements.define('ig-cores', IgCores);
  if (!customElements.get('ig-starfield')) customElements.define('ig-starfield', IgStarfield);

  /* ---------------------------------------------------------- */
  /* Video reviews: YouTube facades + category filters           */
  /* ---------------------------------------------------------- */

  // A thumbnail stands in for each player; the real (privacy-enhanced) player loads only on click.
  // Only one video plays at a time: starting another puts the previous one back to its thumbnail.
  const resetVideo = facade => {
    if (!facade || !facade.igHtml) return;
    facade.innerHTML = facade.igHtml;
    facade.classList.remove('is-playing');
    facade.closest('.ig-videos__card, .ig-videos__featured')?.classList.remove('is-playing');
  };

  const playVideo = facade => {
    if (!facade || facade.classList.contains('is-playing')) return;
    const id = facade.dataset.igVideo;
    if (!/^[\w-]{6,20}$/.test(id || '')) return;
    document.querySelectorAll('.ig-video.is-playing').forEach(resetVideo);
    facade.igHtml = facade.innerHTML;
    facade.closest('.ig-videos__card, .ig-videos__featured')?.classList.add('is-playing');
    const frame = document.createElement('iframe');
    frame.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
    frame.title = facade.dataset.title || 'Video';
    frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    frame.allowFullscreen = true;
    frame.referrerPolicy = 'strict-origin-when-cross-origin';
    facade.classList.add('is-playing');
    facade.querySelector('.ig-video__play')?.remove();
    facade.appendChild(frame);
  };

  document.addEventListener('click', e => {
    const btn = e.target.closest && e.target.closest('.ig-video__play');
    if (btn) playVideo(btn.closest('.ig-video'));
  });

  class IgVideos extends HTMLElement {
    connectedCallback() {
      this.filters = [...this.querySelectorAll('[data-filter]')];
      this.cards = [...this.querySelectorAll('.ig-videos__card:not([data-always])')];
      // a card can sit in several groups: "replica xeno creator"
      this.inGroup = (card, cat) => (card.dataset.category || '').split(/\s+/).includes(cat);
      this.filters.forEach(btn => {
        const n = btn.dataset.filter === 'all' ? this.cards.length : this.cards.filter(c => this.inGroup(c, btn.dataset.filter)).length;
        if (!n) { btn.hidden = true; return; }
        if (!btn.querySelector('small')) btn.append(Object.assign(document.createElement('small'), { textContent: n }));
        btn.addEventListener('click', () => this.filter(btn.dataset.filter));
      });
    }

    filter(cat) {
      this.filters.forEach(btn => btn.setAttribute('aria-pressed', String(btn.dataset.filter === cat)));
      this.cards.forEach(card => {
        const hide = cat !== 'all' && !this.inGroup(card, cat);
        if (hide) resetVideo(card.querySelector('.ig-video.is-playing'));
        card.hidden = hide;
      });
    }
  }

  if (!customElements.get('ig-videos')) customElements.define('ig-videos', IgVideos);

  /* ---------------------------------------------------------- */
  /* FAQ: instant search, topic menu that follows the scroll     */
  /* ---------------------------------------------------------- */

  class IgFaq extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-faq-item]')];
      this.groups = [...this.querySelectorAll('[data-faq-group]')];
      this.links = [...this.querySelectorAll('[data-faq-link]')];
      this.nav = this.querySelector('.ig-faq__nav');
      this.empty = this.querySelector('[data-faq-empty]');
      this.input = this.querySelector('[data-faq-search]');
      this.items.forEach(item => {
        item.q = item.querySelector('.ig-faq__q span');
        item.qText = item.q ? item.q.textContent : '';
        item.haystack = item.textContent.replace(/\s+/g, ' ').toLowerCase();
      });

      let t;
      this.input?.addEventListener('input', () => {
        clearTimeout(t);
        t = setTimeout(() => this.search(this.input.value), 80);
      });

      if ('IntersectionObserver' in window && this.links.length) {
        this.visible = new Map();
        this.spy = new IntersectionObserver(entries => {
          entries.forEach(e => this.visible.set(e.target, e.isIntersecting));
          const current = this.groups.find(g => this.visible.get(g));
          if (current) this.mark(current.id);
        }, { rootMargin: '-25% 0px -65% 0px' });
        this.groups.forEach(g => g.id && this.spy.observe(g));
      }

      this.openFromHash();
      this.onHash = () => this.openFromHash();
      window.addEventListener('hashchange', this.onHash);
    }

    disconnectedCallback() {
      this.spy?.disconnect();
      window.removeEventListener('hashchange', this.onHash);
    }

    mark(id) {
      this.links.forEach(link => {
        const on = link.getAttribute('href') === `#${id}`;
        link.setAttribute('aria-current', String(on));
        // keep the active chip in view on phones (the menu scrolls sideways there)
        if (on && this.nav && this.nav.scrollWidth > this.nav.clientWidth) {
          const left = link.offsetLeft - (this.nav.clientWidth - link.offsetWidth) / 2;
          this.nav.scrollTo({ left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        }
      });
    }

    openFromHash() {
      const id = decodeURIComponent(window.location.hash.slice(1));
      const item = id && this.querySelector(`[data-faq-item][id="${CSS.escape(id)}"]`);
      if (item) item.open = true;
    }

    highlight(item, terms) {
      if (!item.q) return;
      if (!terms.length) { item.q.textContent = item.qText; return; }
      const re = new RegExp(`(${terms.map(x => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
      item.q.replaceChildren(...item.qText.split(re).map((part, i) => (i % 2 ? Object.assign(document.createElement('mark'), { textContent: part }) : document.createTextNode(part))));
    }

    search(raw) {
      const terms = raw.trim().toLowerCase().split(/\s+/).filter(x => x.length > 1);
      let shown = 0;
      this.items.forEach(item => {
        const match = !terms.length || terms.every(x => item.haystack.includes(x));
        item.hidden = !match;
        this.highlight(item, match ? terms : []);
        if (match) shown += 1;
      });
      // with only a few results, open them so the answer is right there
      this.items.forEach(item => {
        if (terms.length && !item.hidden && shown <= 3) {
          if (!item.open) item.dataset.searchOpened = '';
          item.open = true;
        } else if ('searchOpened' in item.dataset) {
          item.open = false;
          delete item.dataset.searchOpened;
        }
      });
      this.groups.forEach(g => { g.hidden = !g.querySelector('[data-faq-item]:not([hidden])'); });
      if (this.empty) this.empty.hidden = shown > 0;
    }
  }

  if (!customElements.get('ig-faq')) customElements.define('ig-faq', IgFaq);

  /* ---------------------------------------------------------- */
  /* Instagram reels: muted previews + player overlay            */
  /* ---------------------------------------------------------- */

  const reelCode = url => ((url || '').match(/instagram\.com\/(?:[\w.]+\/)?(?:reels?|p|tv)\/([\w-]+)/) || [])[1];
  let reelBox = null;
  let reelReturn = null;

  const closeReel = () => {
    if (!reelBox || reelBox.hidden) return;
    reelBox.classList.remove('is-open', 'is-loaded');
    root.classList.remove('ig-reel-open');
    setTimeout(() => {
      reelBox.hidden = true;
      reelBox.querySelector('.ig-reelbox__body iframe')?.remove(); // stops playback
    }, reduceMotion.matches ? 0 : 320);
    reelReturn?.focus?.({ preventScroll: true });
  };

  // Plays a reel through Instagram's official embed. Anything that isn't a post/reel link just opens on Instagram.
  const openReel = (url, title) => {
    const code = reelCode(url);
    if (!code) { window.open(url, '_blank', 'noopener'); return; }
    reelReturn = document.activeElement;
    if (!reelBox) {
      reelBox = document.createElement('div');
      reelBox.className = 'ig-reelbox';
      reelBox.hidden = true;
      reelBox.innerHTML = '<div class="ig-reelbox__backdrop" data-reel-close></div>'
        + '<div class="ig-reelbox__panel" role="dialog" aria-modal="true">'
        + '<div class="ig-reelbox__top"><span class="ig-reelbox__title"></span><span class="ig-reelbox__actions">'
        + '<a class="ig-reelbox__link" target="_blank" rel="noopener">Open on Instagram</a>'
        + '<button type="button" class="ig-reelbox__close" data-reel-close aria-label="Close"><svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button>'
        + '</span></div><div class="ig-reelbox__body"><div class="ig-reelbox__loading"><span></span>Loading reel</div></div></div>';
      document.body.appendChild(reelBox);
      reelBox.addEventListener('click', e => { if (e.target.closest('[data-reel-close]')) closeReel(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape') closeReel(); });
    }
    reelBox.querySelector('.ig-reelbox__title').textContent = title || 'Instagram reel';
    reelBox.querySelector('.ig-reelbox__panel').setAttribute('aria-label', title || 'Instagram reel');
    reelBox.querySelector('.ig-reelbox__link').href = url;
    const frame = document.createElement('iframe');
    frame.src = `https://www.instagram.com/p/${code}/embed/`;
    frame.title = title || 'Instagram reel';
    frame.allow = 'autoplay; encrypted-media; picture-in-picture; clipboard-write';
    frame.allowFullscreen = true;
    frame.addEventListener('load', () => reelBox.classList.add('is-loaded'), { once: true });
    const body = reelBox.querySelector('.ig-reelbox__body');
    body.querySelector('iframe')?.remove();
    body.appendChild(frame);
    reelBox.hidden = false;
    void reelBox.offsetWidth;
    reelBox.classList.add('is-open');
    root.classList.add('ig-reel-open');
    reelBox.querySelector('.ig-reelbox__close').focus({ preventScroll: true });
  };

  // Any reel link on the site (reels row, product page) plays in the overlay
  document.addEventListener('click', e => {
    const a = e.target.closest && e.target.closest('a[data-reel-open]');
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    const frame = a.closest('[data-reel]');
    openReel((frame && frame.dataset.igUrl) || a.href, frame && frame.dataset.title);
  });

  class IgSocial extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.ig-social__track');
      if (!this.track) return;

      // Uploaded videos: play muted while on screen, pause when scrolled away
      this.videos = [...this.querySelectorAll('.ig-reel__frame video')];
      this.videos.forEach(v => { v.muted = true; v.playsInline = true; });
      if (this.videos.length && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver(entries => entries.forEach(e => {
          if (e.isIntersecting && !reduceMotion.matches) e.target.play().catch(() => {});
          else e.target.pause();
        }), { threshold: 0.6 });
        this.videos.forEach(v => this.io.observe(v));
      }
      this.querySelectorAll('[data-reel-sound]').forEach(btn => btn.addEventListener('click', () => {
        const video = btn.closest('.ig-reel__frame').querySelector('video');
        if (!video) return;
        const on = video.muted;
        this.videos.forEach(v => { v.muted = true; });
        this.querySelectorAll('[data-reel-sound]').forEach(b => { b.setAttribute('aria-pressed', 'false'); b.setAttribute('aria-label', 'Turn sound on'); });
        video.muted = !on;
        if (on) { video.play().catch(() => {}); btn.setAttribute('aria-pressed', 'true'); btn.setAttribute('aria-label', 'Turn sound off'); }
      }));

      const prev = this.querySelector('[data-reel-prev]');
      const next = this.querySelector('[data-reel-next]');
      const step = () => this.track.clientWidth * 0.8;
      prev?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      this.update = () => {
        const over = this.track.scrollWidth > this.track.clientWidth + 4;
        this.classList.toggle('has-overflow', over);
        if (prev) prev.disabled = this.track.scrollLeft < 8;
        if (next) next.disabled = this.track.scrollLeft + this.track.clientWidth > this.track.scrollWidth - 8;
      };
      this.track.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      this.update();
    }

    disconnectedCallback() {
      this.io?.disconnect();
      window.removeEventListener('resize', this.update);
    }
  }

  if (!customElements.get('ig-social')) customElements.define('ig-social', IgSocial);

  /* ---------------------------------------------------------- */
  /* Saber quiz sheet: opens the Saber Finder over any page      */
  /* ---------------------------------------------------------- */

  // Any link marked data-saber-quiz (or pointing at the finder page) opens the finder in a sheet.
  // The finder is rendered by Shopify's Section Rendering API, so prices and products stay live.
  // If the request fails, the link simply navigates as normal.
  let quiz = null;
  let quizReturn = null;

  const closeQuiz = () => {
    if (!quiz || quiz.hidden) return;
    quiz.classList.remove('is-open');
    root.classList.remove('ig-quiz-open');
    setTimeout(() => { quiz.hidden = true; }, reduceMotion.matches ? 0 : 320);
    quizReturn?.focus?.({ preventScroll: true });
  };

  const openQuiz = async href => {
    quizReturn = document.activeElement;
    if (!quiz) {
      quiz = document.createElement('div');
      quiz.className = 'ig-quiz';
      quiz.hidden = true;
      quiz.innerHTML = '<div class="ig-quiz__backdrop" data-quiz-close></div>'
        + '<div class="ig-quiz__panel" role="dialog" aria-modal="true" aria-label="Saber Finder">'
        + '<div class="ig-quiz__top"><span class="ig-quiz__label"><i aria-hidden="true"></i>Saber Finder</span>'
        + '<button type="button" class="ig-quiz__close" data-quiz-close aria-label="Close"><svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button></div>'
        + '<div class="ig-quiz__body"><div class="ig-quiz__loading" aria-live="polite"><span></span>Loading the finder</div></div></div>';
      document.body.appendChild(quiz);
      quiz.addEventListener('click', e => { if (e.target.closest('[data-quiz-close]')) closeQuiz(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape') closeQuiz(); });
    }

    const body = quiz.querySelector('.ig-quiz__body');
    quiz.hidden = false;
    void quiz.offsetWidth;
    quiz.classList.add('is-open');
    root.classList.add('ig-quiz-open');
    quiz.querySelector('.ig-quiz__close').focus({ preventScroll: true });

    if (body.querySelector('ig-finder')) return;
    try {
      const base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const res = await fetch(`${base}?section_id=ig-finder`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const finder = doc.querySelector('ig-finder');
      if (!finder || !finder.querySelector('.ig-finder__pool')) throw new Error('empty');
      finder.removeAttribute('id');
      finder.classList.add('is-inview');
      body.replaceChildren(document.importNode(finder, true));
    } catch (err) {
      closeQuiz();
      window.location.href = href;
    }
  };

  document.addEventListener('click', e => {
    const link = e.target.closest && e.target.closest('a[data-saber-quiz], a[href*="/pages/saber-finder"]');
    if (!link || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (window.location.pathname.indexOf('/pages/saber-finder') > -1) return;
    e.preventDefault();
    openQuiz(link.href);
  });

  /* ---------------------------------------------------------- */
  /* Delivery estimate under the add-to-cart button              */
  /* ---------------------------------------------------------- */

  // Adds business days (Mon–Fri) to today and prints the window, e.g. "Oct 6 – Oct 12".
  const addBusinessDays = (date, days) => {
    const d = new Date(date);
    while (days > 0) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) days -= 1;
    }
    return d;
  };

  const renderEta = () => {
    document.querySelectorAll('[data-ig-eta]:not([data-ready])').forEach(el => {
      const min = parseInt(el.dataset.min, 10) || 8;
      const max = parseInt(el.dataset.max, 10) || 12;
      const fmt = new Intl.DateTimeFormat(root.lang || undefined, { month: 'short', day: 'numeric' });
      const now = new Date();
      const out = el.querySelector('[data-eta-range]');
      if (!out) return;
      out.textContent = `${fmt.format(addBusinessDays(now, min))} – ${fmt.format(addBusinessDays(now, max))}`;
      el.hidden = false;
      el.dataset.ready = '';
    });
  };

  renderEta();
  document.addEventListener('product:rerender', () => setTimeout(renderEta, 0));
  document.addEventListener('shopify:section:load', renderEta);

  /* Product page HUD: image counter + scan sweep when the gallery changes */
  document.addEventListener('carousel:change', e => {
    const gallery = e.target.closest && e.target.closest('product-gallery');
    const hud = gallery && gallery.querySelector('.ig-hud');
    if (!hud) return;
    const out = hud.querySelector('[data-hud-index]');
    const slides = [...gallery.querySelectorAll('.product-gallery__media:not([hidden])')];
    let i = e.detail && e.detail.slide ? slides.indexOf(e.detail.slide) : (e.detail ? e.detail.index : -1);
    if (out && i > -1) out.textContent = String(i + 1).padStart(2, '0');
    if (reduceMotion.matches) return;
    hud.classList.remove('is-scanning');
    void hud.offsetWidth;
    hud.classList.add('is-scanning');
  });

  /* ---------------------------------------------------------- */
  /* Product page round 6: spec sheet, compare builds, kit builder */
  /* ---------------------------------------------------------- */

  const igMoney = cents => {
    const decode = document.createElement('textarea');
    decode.innerHTML = String((window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || '${{amount}}').replace(/<[^>]*>/g, '');
    const fmt = decode.value;
    const value = Number(cents || 0) / 100;
    const group = (decimals, thousands = ',', point = '.') => {
      const [int, frac] = value.toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? point + frac : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(0);
        case 'amount_with_comma_separator': return group(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(2, "'", '.');
        case 'amount_with_space_separator': return group(2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return group(0, ' ', ',');
        default: return group(2);
      }
    });
  };

  // The buy box form of the product this section belongs to. The variant picker names it (form-id); the payment-terms
  // block renders a second form whose id starts the same way ("...-product-installment-form"), so never match by prefix alone.
  const mainForm = productId => {
    const prefix = `product-form-${productId}-`;
    const picker = [...document.querySelectorAll('.shopify-section--main-product variant-picker[form-id]')]
      .find(p => (p.getAttribute('form-id') || '').startsWith(prefix));
    const named = picker && document.getElementById(picker.getAttribute('form-id'));
    if (named) return named;
    return document.querySelector(`.shopify-section--main-product form[is="product-form"][id^="${prefix}"]`)
      || [...document.querySelectorAll(`form[id^="${prefix}"]`)].find(f => !/installment/.test(f.getAttribute('id')));
  };

  // Switch an option in the buy box (the theme then re-renders price, stock, gallery and the sticky bar)
  // Inputs carry the option value's id (newer themes) or its name; match either
  const pickOption = (productId, position, value, valueId) => {
    const form = mainForm(productId);
    if (!form) return false;
    const formId = form.getAttribute('id');
    const inputs = [...document.querySelectorAll(`input[data-option-position="${position}"][form="${formId}"]`)].filter(i => !i.disabled);
    const input = inputs.find(i => valueId && i.value === String(valueId)) || inputs.find(i => i.value === value)
      || inputs.find(i => i.labels && i.labels[0] && i.labels[0].querySelector('.ally-option-name') && i.labels[0].querySelector('.ally-option-name').firstChild && i.labels[0].querySelector('.ally-option-name').firstChild.textContent.trim() === value);
    if (input) {
      if (!input.checked) input.click();
      return true;
    }
    const select = document.querySelector(`select[data-option-position="${position}"][form="${formId}"]`);
    const opt = select && ([...select.options].find(o => valueId && o.value === String(valueId)) || [...select.options].find(o => o.value === value || o.textContent.trim() === value));
    if (opt) {
      select.value = opt.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    return false;
  };

  // cb(variant) runs when the buy box switches build; variant is null for a combination that doesn't exist
  const onMainVariant = (productId, cb) => document.addEventListener('variant:change', e => {
    const form = mainForm(productId);
    if (form && e.target === form && e.detail && 'variant' in e.detail) cb(e.detail.variant || null);
  });

  // Core lab on a product page: "Choose" buttons set the core in the buy box, and the side-by-side table follows the
  // build (exact price for the current hilt and blade, the current core's column lit, "In your build" states).
  class BuildCompare {
    constructor(root, onCore) {
      this.root = root;
      this.onCore = onCore;
      this.pid = root.dataset.productId;
      this.corePos = Number(root.dataset.corePosition || 0);
      try { this.variants = JSON.parse(root.querySelector('script[data-core-variants]').textContent); } catch (e) { this.variants = []; }

      root.addEventListener('click', e => {
        const btn = e.target.closest('[data-spec-pick]');
        if (!btn || !root.contains(btn) || btn.getAttribute('aria-pressed') === 'true') return;
        this.pick(Number(btn.dataset.position), btn.dataset.value, btn.dataset.valueId);
      });
      onMainVariant(this.pid, v => { if (v) this.sync(v.options || [], true); });

      // start from the build the buy box shows (the picker's own variant data), else the server-rendered one
      let start = null;
      try {
        const data = document.querySelector('.shopify-section--main-product variant-picker script[data-variant]');
        const v = data && JSON.parse(data.textContent);
        if (v && v.options) start = v.options;
      } catch (e) { /* fall through */ }
      if (!start) {
        const v = this.variants.find(x => String(x.id) === root.dataset.current) || this.variants[0];
        if (v) start = v.o;
      }
      if (start) this.sync(start, false);
    }

    pick(position, value, valueId) {
      if (!pickOption(this.pid, position, value, valueId)) {
        // No buy box on this page (or a custom picker): go to the closest matching variant instead
        const opts = (this.current || []).slice();
        opts[position - 1] = value;
        const match = this.variants.find(v => v.o.every((o, i) => o === opts[i])) || this.variants.find(v => v.o[position - 1] === value);
        if (match) window.location.href = `${this.root.dataset.productUrl}?variant=${match.id}`;
        return;
      }
      // optimistic feedback while the theme fetches the new variant
      const opts = (this.current || []).slice();
      opts[position - 1] = value;
      this.sync(opts, true);
    }

    sync(opts, fromBuild) {
      this.current = opts;
      const ci = this.corePos - 1;
      const root = this.root;
      root.querySelectorAll('[data-spec-pick]').forEach(btn => {
        btn.setAttribute('aria-pressed', String(opts[Number(btn.dataset.position) - 1] === btn.dataset.value));
      });
      const cols = [...root.querySelectorAll('.ig-compare__col')];
      cols.forEach(col => {
        if (col.igFrom === undefined) {
          const out = col.querySelector('[data-compare-price]');
          col.igFrom = out ? out.textContent : '';
          const idle = col.querySelector('.ig-compare__choose-idle');
          col.igIdle = idle ? idle.textContent : 'Choose';
          col.igSoldout = col.querySelector('[data-soldout]') !== null;
        }
      });
      cols.forEach((col, n) => {
        const on = opts[ci] === col.dataset.value;
        col.classList.toggle('is-current', on);
        root.querySelectorAll(`td[data-col="${n}"]`).forEach(td => td.classList.toggle('is-current', on));
        const value = col.dataset.value;
        const exact = this.variants.find(v => v.o[ci] === value && v.o.every((o, i) => i === ci || opts[i] === undefined || o === opts[i]));
        const out = col.querySelector('[data-compare-price]');
        const from = col.querySelector('[data-compare-from]');
        const idle = col.querySelector('.ig-compare__choose-idle');
        if (!exact) {
          // this hilt/blade isn't offered with that core: back to the overall "From" price
          if (out) out.textContent = col.igFrom;
          if (from) from.hidden = false;
          col.classList.toggle('is-soldout', col.igSoldout);
          if (idle) idle.textContent = col.igIdle;
          return;
        }
        if (out) out.textContent = igMoney(exact.p);
        if (from) from.hidden = true;
        col.classList.toggle('is-soldout', !exact.a);
        if (idle) idle.textContent = exact.a ? 'Choose' : 'Sold out';
      });
      const active = cols.find(col => opts[ci] === col.dataset.value)
        || [...root.querySelectorAll('[data-spec-pick]')].find(b => opts[ci] === b.dataset.value && b.closest('[data-core]'));
      const holder = active && (active.matches('[data-core]') ? active : active.closest('[data-core]'));
      const core = holder ? holder.dataset.core : '';
      if (fromBuild && core && this.onCore) this.onCore(core);
    }
  }

  // "base" or "neo" for a saber variant's options (Base Lit vs Xenopixel / Proffie / Neopixel)
  const coreOf = value => {
    const o = String(value || '').toLowerCase();
    if (o.includes('base') || o.includes('rgb')) return 'base';
    if (o.includes('xeno') || o.includes('proffie') || o.includes('neo') || o.includes('pixel')) return 'neo';
    return '';
  };

  // blade length in inches from an option or variant title: '32" Standard', "28'' (72cm)", '36"' -> 32 / 28 / 36
  const lenOf = value => {
    const m = String(value || '').match(/(\d{2})\s*(?:"|''|″|”|in\b|inch)/i);
    return m ? Number(m[1]) : 0;
  };

  // a build that is really two hilts (Eco Origin "Saber Staff Bundle (2)")
  const multiOf = value => /staff|bundle|pair|\(2\)/i.test(String(value || ''));

  class IgBundle extends HTMLElement {
    connectedCallback() {
      this.pid = this.dataset.productId;
      this.kitLive = this.hasAttribute('data-kit-live');
      this.core = this.dataset.core || '';
      this.corePos = Number(this.dataset.corePosition || 0);
      this.main = { id: this.dataset.mainId, price: Number(this.dataset.mainPrice || 0), available: this.dataset.mainAvailable !== 'false' };
      this.lenPos = Number(this.dataset.lenPosition || 0);
      this.cfgPos = Number(this.dataset.configPosition || 0);
      this.len = Number(this.dataset.len || 0);
      this.multi = this.hasAttribute('data-multi');
      this.button = this.querySelector('[data-bundle-add]');
      this.allButton = this.querySelector('[data-bundle-all]');
      this.addons = this.querySelector('[data-bundle-addons]');
      this.moreWrap = this.querySelector('[data-bundle-more-wrap]');
      this.moreList = this.querySelector('[data-bundle-more]');
      this.moreToggle = this.querySelector('[data-bundle-more-toggle]');
      this.bind();
      this.evaluate(false);
      this.layout();
      this.setupPeek();
      onMainVariant(this.pid, v => this.setMain(v));
      this.button?.addEventListener('click', () => this.add());
      this.moreToggle?.addEventListener('click', () => this.setMoreOpen(this.moreList.hidden));
      // "Add the full kit": the main row only, what's on screen
      this.allButton?.addEventListener('click', () => {
        this.mainChecks().forEach(c => { c.checked = true; });
        this.update();
        this.button?.focus();
      });
      this.update();
    }

    mainChecks() {
      return this.addons ? [...this.addons.querySelectorAll('[data-bundle-check]')].filter(c => !c.disabled && !c.closest('[data-bundle-item]').hidden) : [];
    }

    setMoreOpen(open) {
      if (!this.moreList) return;
      this.moreList.hidden = !open;
      this.moreToggle?.setAttribute('aria-expanded', String(open));
      this.classList.toggle('is-more-open', open);
    }

    // Short-lived line under the receipt, e.g. why an extra was taken out of the kit
    say(text) {
      const n = this.querySelector('[data-bundle-notice]');
      if (!n) return;
      n.textContent = text;
      n.hidden = false;
      clearTimeout(this.sayTimer);
      this.sayTimer = setTimeout(() => { n.hidden = true; }, 8000);
    }

    // Build-level fit (the rest was settled on the server): spare blade needs a pixel core and a blade in the saber's
    // length, the 7/8" plug is Base Lit only, the hilt bag holds one hilt. A ticked extra that stops fitting comes out
    // of the kit, with a note saying why.
    evaluate(announce) {
      const removed = [];
      this.querySelectorAll('[data-bundle-item][data-rule]').forEach(item => {
        const rule = item.dataset.rule;
        const check = item.querySelector('[data-bundle-check]');
        const fit = item.querySelector('[data-bundle-fit]');
        let why = '';
        if (rule === 'spare_blade') {
          if (this.core !== 'neo') {
            why = 'it needs a Xenopixel or Proffie core';
          } else {
            const pick = (item.igVariants || []).find(v => v.available && lenOf(v.title) === this.len);
            if (!pick) {
              why = this.len ? `it isn’t available in ${this.len}″` : 'it needs a blade length';
            } else {
              const select = item.querySelector('[data-bundle-variant]');
              if (select) select.value = String(pick.id);
              this.setVariant(item, pick);
              if (fit) {
                fit.textContent = `Matches your ${this.len}″ blade`;
                fit.hidden = false;
              }
            }
          }
        } else if (rule === 'plug78') {
          if (this.core !== 'base') why = 'it fits Base Lit sabers only';
        } else if (rule === 'bag') {
          if (this.multi) why = 'it holds a single hilt';
        }
        if (why) {
          if (check && check.checked && !item.hidden) {
            removed.push(`${(item.querySelector('.ig-bundle__name') || {}).textContent || 'An extra'} was removed: ${why}.`);
          }
          if (check) check.checked = false;
          item.setAttribute('data-unfit', '');
        } else {
          item.removeAttribute('data-unfit');
        }
      });
      if (announce && removed.length) this.say(removed.join(' '));
    }

    // Extras in order of preference: "Always" ones first (theme editor order), then the rotation, shuffled once per visit
    buildOrder() {
      const all = [...this.querySelectorAll('.ig-bundle__addons [data-bundle-item]')];
      const rotate = all.filter(el => el.dataset.slot !== 'always');
      for (let i = rotate.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [rotate[i], rotate[j]] = [rotate[j], rotate[i]];
      }
      this.order = all.filter(el => el.dataset.slot === 'always').concat(rotate);
    }

    itemPrice(item) {
      const c = item.querySelector('[data-bundle-check]');
      return c ? this.price({ price: Number(c.dataset.price || 0), kit: Number(c.dataset.kit || 0) }) : 0;
    }

    // Which extras show. Main row: at most one per group (e.g. one stand), none pricier than the cap (a share of the
    // saber's price, so a $130 stand isn't pushed next to a $90 saber), up to the max, listed cheapest first.
    // Everything else that fits goes under "More add-ons". Runs again when the build changes: what's ticked or already
    // in the main row stays there while it fits.
    layout() {
      if (!this.order) this.buildOrder();
      if (!this.order.length) return;
      const max = Number(this.dataset.max || this.order.length);
      const cap = Number(this.dataset.priceCap || 0);
      const limit = cap > 0 && this.main.price > 0 ? this.main.price * cap / 100 : Infinity;
      const ticked = el => !!el.querySelector('[data-bundle-check]:checked');
      // a shopper's own ticks stay even when a cheaper build lowers the cap; on the first pass ("Tick extras by
      // default") only what makes the main row stays ticked
      const first = !this.laidOut;
      const pool = this.order.filter(el => !el.hasAttribute('data-unfit') && ((!first && ticked(el)) || this.itemPrice(el) <= limit));
      const mainPool = pool.filter(el => el.dataset.place !== 'more');
      const inMain = el => el.parentElement === this.addons && !el.hidden;
      const ranked = this.laidOut
        ? [...mainPool.filter(el => inMain(el) && ticked(el)), ...mainPool.filter(el => inMain(el) && !ticked(el)), ...mainPool.filter(el => !inMain(el))]
        : mainPool;
      this.laidOut = true;
      const groups = new Set();
      const keep = [];
      ranked.forEach(el => {
        const g = el.dataset.group;
        if (keep.length >= max || (g && groups.has(g))) return;
        keep.push(el);
        if (g) groups.add(g);
      });
      // cheapest first; the carrying case always closes the row
      const rank = el => this.itemPrice(el) + (el.dataset.rule === 'case' ? 1e9 : 0);
      const byPrice = (x, y) => rank(x) - rank(y);
      keep.sort(byPrice);
      const more = this.moreList ? pool.filter(el => !keep.includes(el)).sort(byPrice) : [];
      this.order.forEach(el => {
        const on = keep.includes(el) || more.includes(el);
        el.hidden = !on;
        if (!on || (first && !keep.includes(el))) {
          const c = el.querySelector('[data-bundle-check]');
          if (c) c.checked = false;
        }
      });
      // the page order follows the price order, so reading and tabbing match what's on screen
      const place = (box, list) => {
        if (!box) return;
        const now = [...box.children].filter(el => el.matches('[data-bundle-item]') && !el.hidden);
        if (now.length !== list.length || now.some((el, n) => el !== list[n])) list.forEach(el => box.appendChild(el));
      };
      place(this.addons, keep);
      place(this.moreList, more);
      if (this.addons) this.addons.style.setProperty('--n', keep.length);
      if (this.moreWrap) {
        this.moreWrap.hidden = more.length === 0;
        const count = this.querySelector('[data-bundle-more-count]');
        if (count) count.textContent = more.length ? String(more.length) : '';
        if (!more.length) this.setMoreOpen(false);
        else if (more.some(ticked)) this.setMoreOpen(true);
      }
    }

    bind() {
      this.querySelectorAll('[data-bundle-item]').forEach(item => {
        if (item.igBound) return;
        item.igBound = true;
        try { item.igVariants = JSON.parse(item.querySelector('script[data-bundle-variants]').textContent); } catch (e) { item.igVariants = []; }
        const check = item.querySelector('[data-bundle-check]');
        const select = item.querySelector('[data-bundle-variant]');
        check?.addEventListener('change', () => this.update());
        select?.addEventListener('change', () => {
          const v = item.igVariants.find(x => String(x.id) === select.value);
          if (v) this.setVariant(item, v);
          check.checked = true;
          this.update();
        });
        if (item.hasAttribute('data-match-core')) this.fitCore(item, false);
      });
    }

    price(v) {
      return this.kitLive && v.kit > 0 ? v.kit : v.price;
    }

    setVariant(item, v) {
      const check = item.querySelector('[data-bundle-check]');
      check.value = v.id;
      check.dataset.price = v.price;
      check.dataset.kit = v.kit || 0;
      if (item.dataset.match) {
        // necklace: say so while the saber's own design is picked
        const fit = item.querySelector('[data-bundle-fit]');
        if (fit) fit.hidden = String(v.id) !== item.dataset.match;
      }
      const img = item.querySelector('.ig-bundle__media img');
      if (img && v.image && img.getAttribute('src') !== v.image) {
        img.removeAttribute('srcset');
        img.src = v.image;
      }
      const now = item.querySelector('[data-bundle-price]');
      const was = item.querySelector('[data-bundle-was]');
      if (now) now.textContent = igMoney(this.price(v));
      if (was) {
        was.textContent = igMoney(v.price);
        was.hidden = !(this.kitLive && v.kit > 0);
      }
    }

    // Blade plugs, batteries...: only offer the version that fits the saber's core
    fitCore(item, keepChoice = true) {
      const select = item.querySelector('[data-bundle-variant]');
      const check = item.querySelector('[data-bundle-check]');
      const all = item.igVariants || [];
      const fits = all.filter(v => !v.core || !this.core || v.core === this.core);
      const list = fits.length ? fits : all;
      const current = list.find(v => String(v.id) === check.value);
      let pick = keepChoice && current ? current : null;
      if (!pick) {
        // same colour/finish in the other core's version, when there is one
        const was = all.find(v => String(v.id) === check.value);
        pick = (was && list.find(v => v.available && v.short && v.short === was.short)) || list.find(v => v.available) || list[0];
      }
      if (select) {
        select.replaceChildren(...list.map(v => {
          const o = document.createElement('option');
          o.value = v.id;
          o.textContent = (v.short || v.title) + (v.available ? '' : ' · sold out');
          o.disabled = !v.available;
          o.selected = pick && v.id === pick.id;
          return o;
        }));
        select.hidden = list.length < 2;
      }
      const fit = item.querySelector('[data-bundle-fit]');
      const out = !pick || !pick.available;
      if (fit) fit.textContent = out ? `Sold out for ${this.core === 'base' ? 'Base Lit' : 'Neopixel'}` : (this.core === 'base' ? 'Fits Base Lit' : 'Fits Neopixel');
      check.disabled = out;
      if (out) check.checked = false;
      item.classList.toggle('is-soldout', out);
      if (select) select.disabled = out;
      if (pick) this.setVariant(item, pick);
    }

    setMain(v) {
      if (!v) {
        // the buy box landed on a combination that doesn't exist
        this.main = { ...this.main, available: false, missing: true };
        this.update();
        return;
      }
      this.main = { id: String(v.id), price: Number(v.price), available: !!v.available };
      const title = this.querySelector('[data-bundle-main-title]');
      if (title) title.textContent = v.title || '';
      const price = this.querySelector('[data-bundle-main-price]');
      if (price) price.textContent = igMoney(v.price);
      const src = v.featured_media && v.featured_media.preview_image && v.featured_media.preview_image.src;
      const img = this.querySelector('.ig-bundle__item--main img');
      if (img) {
        if (!this.firstImage) this.firstImage = { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') };
        if (src) {
          img.removeAttribute('srcset');
          img.src = src + (src.includes('?') ? '&' : '?') + 'width=360';
        } else {
          img.src = this.firstImage.src;
          if (this.firstImage.srcset) img.setAttribute('srcset', this.firstImage.srcset);
        }
      }
      const opts = v.options || [];
      const core = this.corePos ? coreOf(opts[this.corePos - 1]) : '';
      if (core && core !== this.core) {
        this.core = core;
        this.dataset.core = core;
        this.querySelectorAll('[data-bundle-item][data-match-core]').forEach(item => this.fitCore(item, false));
      }
      if (this.lenPos) this.len = lenOf(opts[this.lenPos - 1]) || this.len;
      if (this.cfgPos) this.multi = multiOf(opts[this.cfgPos - 1]);
      this.evaluate(true);
      this.layout();
      this.update();
    }

    /* ---- Quick look: an extra's photos and details in a sheet, without leaving the page ---- */

    setupPeek() {
      const d = this.querySelector('[data-bundle-peek-dialog]');
      if (!d) return;
      this.peek = d;
      this.gallery = d.querySelector('[data-peek-gallery]');
      this.addEventListener('click', e => {
        const btn = e.target.closest && e.target.closest('[data-bundle-peek]');
        if (btn) {
          e.preventDefault();
          this.peekFrom = btn;
          this.openPeek(btn.closest('[data-bundle-item]'));
        }
      });
      d.addEventListener('click', e => {
        if (e.target === d || e.target.closest('[data-peek-close]')) this.closePeek();
        const step = e.target.closest('[data-peek-step]');
        if (step) this.peekGo(this.peekIndex() + Number(step.dataset.peekStep));
        const dot = e.target.closest('[data-peek-dot]');
        if (dot) this.peekGo(Number(dot.dataset.peekDot));
      });
      d.addEventListener('close', () => {
        document.documentElement.classList.remove('ig-peek-open');
        if (this.peekFrom && document.contains(this.peekFrom)) this.peekFrom.focus({ preventScroll: true });
      });
      d.querySelector('[data-peek-toggle]')?.addEventListener('click', () => {
        const item = this.peekItem;
        const c = item && item.querySelector('[data-bundle-check]');
        if (!c || c.disabled) return;
        c.checked = !c.checked;
        this.update();
        this.closePeek();
      });
      let raf = 0;
      this.gallery.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.peekDots());
      }, { passive: true });
      this.gallery.addEventListener('keydown', e => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          this.peekGo(this.peekIndex() + (e.key === 'ArrowRight' ? 1 : -1));
        }
      });
    }

    async productData(handle) {
      this.products = this.products || {};
      if (!this.products[handle]) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        this.products[handle] = fetch(`${root}products/${encodeURIComponent(handle)}.js`).then(r => {
          if (!r.ok) throw new Error(String(r.status));
          return r.json();
        }).catch(e => { delete this.products[handle]; throw e; });
      }
      return this.products[handle];
    }

    peekImage(src, alt, width = 900) {
      const img = document.createElement('img');
      let url = String(src || '');
      if (url.startsWith('//')) url = 'https:' + url;
      if (!/[?&]width=/.test(url)) url += (url.includes('?') ? '&' : '?') + `width=${width}`;
      img.src = url;
      img.alt = alt;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.dataset.base = url.split('?')[0].replace(/^https?:/, '');
      return img;
    }

    // Product descriptions can hold anything; keep their words only (paragraphs, headings, bullet lists)
    cleanDescription(html) {
      const doc = new DOMParser().parseFromString(html || '', 'text/html');
      doc.querySelectorAll('script, style, noscript, template, iframe').forEach(n => n.remove());
      const out = [];
      let list = null;
      doc.body.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6').forEach(el => {
        if (el.tagName !== 'LI' && el.closest('li')) return;
        if (el.tagName === 'LI' && el.querySelector('li')) return;
        const text = el.textContent.replace(/\s+/g, ' ').trim();
        if (!text) return;
        if (el.tagName === 'LI') {
          if (!list) { list = document.createElement('ul'); out.push(list); }
          const li = document.createElement('li');
          li.textContent = text;
          list.appendChild(li);
        } else {
          list = null;
          const node = document.createElement(/^H\d$/.test(el.tagName) ? 'h4' : 'p');
          node.textContent = text;
          out.push(node);
        }
      });
      if (!out.length) {
        const text = doc.body.textContent.replace(/\s+/g, ' ').trim();
        if (text) {
          const p = document.createElement('p');
          p.textContent = text;
          out.push(p);
        }
      }
      return out;
    }

    async openPeek(item) {
      const d = this.peek;
      if (!d || !item) return;
      this.peekItem = item;
      const name = (item.querySelector('.ig-bundle__name') || {}).textContent || '';
      d.querySelector('[data-peek-title]').textContent = name.trim();
      this.peekRender(item);
      const thumb = item.querySelector('.ig-bundle__media img');
      this.gallery.replaceChildren(...(thumb ? [this.peekImage(thumb.currentSrc || thumb.src, name.trim())] : []));
      this.gallery.scrollLeft = 0;
      d.querySelector('[data-peek-desc]').replaceChildren();
      this.peekDots();
      d.classList.add('is-loading');
      if (typeof d.showModal === 'function') {
        if (!d.open) d.showModal();
      } else {
        d.setAttribute('open', '');
      }
      document.documentElement.classList.add('ig-peek-open');
      try {
        const data = await this.productData(item.dataset.handle);
        if (this.peekItem !== item) return;
        item.igProduct = data;
        const imgs = (data.images || []).slice(0, 10);
        if (imgs.length) {
          this.gallery.replaceChildren(...imgs.map((src, i) => this.peekImage(src, `${name.trim()}, photo ${i + 1} of ${imgs.length}`)));
        }
        d.querySelector('[data-peek-desc]').replaceChildren(...this.cleanDescription(data.description));
        this.peekShowVariant(item, false);
        this.peekDots();
      } catch (e) {
        // the row's own photo stays; nothing else to show
      } finally {
        if (this.peekItem === item) d.classList.remove('is-loading');
      }
    }

    closePeek() {
      const d = this.peek;
      if (!d) return;
      if (typeof d.close === 'function' && d.open) d.close();
      else {
        d.removeAttribute('open');
        document.documentElement.classList.remove('ig-peek-open');
      }
    }

    peekRender(item) {
      const d = this.peek;
      const c = item.querySelector('[data-bundle-check]');
      const v = { price: Number(c.dataset.price || 0), kit: Number(c.dataset.kit || 0) };
      const now = this.price(v);
      const off = v.price - now;
      d.querySelector('[data-peek-price]').textContent = igMoney(now);
      const was = d.querySelector('[data-peek-was]');
      was.textContent = igMoney(v.price);
      was.hidden = off <= 0;
      const save = d.querySelector('[data-peek-save]');
      save.textContent = off > 0 ? `Kit price · save ${igMoney(off)}` : '';
      save.hidden = off <= 0;
      const fitRow = item.querySelector('[data-bundle-fit]');
      const fit = d.querySelector('[data-peek-fit]');
      fit.textContent = fitRow ? fitRow.textContent : '';
      fit.hidden = !fitRow || fitRow.hidden || !fitRow.textContent.trim();
      fit.dataset.core = this.core;

      // the row's finish / version picker, as buttons
      const opts = d.querySelector('[data-peek-options]');
      const select = item.querySelector('[data-bundle-variant]');
      if (select && !select.hidden && select.options.length > 1) {
        opts.hidden = false;
        opts.replaceChildren(...[...select.options].map(o => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'ig-peek__opt';
          b.textContent = o.textContent;
          b.disabled = o.disabled || select.disabled;
          b.setAttribute('aria-pressed', String(o.value === select.value));
          b.addEventListener('click', () => {
            const pick = (item.igVariants || []).find(x => String(x.id) === o.value);
            if (!pick) return;
            select.value = o.value;
            this.setVariant(item, pick);
            this.update();
            this.peekRender(item);
            this.peekShowVariant(item, true);
          });
          return b;
        }));
      } else {
        opts.hidden = true;
        opts.replaceChildren();
      }

      const t = d.querySelector('[data-peek-toggle]');
      t.disabled = c.disabled;
      t.classList.toggle('is-on', c.checked);
      t.textContent = c.disabled ? 'Sold out' : (c.checked ? 'Remove from kit' : `Add to kit · ${igMoney(now)}`);
    }

    // jump the photos to the chosen finish, when the product has a photo for it
    peekShowVariant(item, smooth) {
      const data = item.igProduct;
      const c = item.querySelector('[data-bundle-check]');
      if (!data || !c) return;
      const v = (data.variants || []).find(x => String(x.id) === c.value);
      const src = v && v.featured_image && v.featured_image.src;
      if (!src) return;
      const base = String(src).split('?')[0].replace(/^https?:/, '');
      const i = [...this.gallery.children].findIndex(img => img.dataset.base === base);
      if (i >= 0) this.peekGo(i, smooth);
    }

    peekIndex() {
      const g = this.gallery;
      return g && g.clientWidth ? Math.round(g.scrollLeft / g.clientWidth) : 0;
    }

    peekGo(i, smooth = true) {
      const g = this.gallery;
      const n = g.children.length;
      if (!n) return;
      const to = Math.max(0, Math.min(n - 1, i));
      g.scrollTo({ left: to * g.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
    }

    peekDots() {
      const d = this.peek;
      const n = this.gallery.children.length;
      const i = this.peekIndex();
      const dots = d.querySelector('[data-peek-dots]');
      if (dots.children.length !== n) {
        dots.replaceChildren(...(n > 1 ? [...Array(n)].map((_, k) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'ig-peek__dot';
          b.dataset.peekDot = k;
          b.setAttribute('aria-label', `Photo ${k + 1}`);
          return b;
        }) : []));
      }
      [...dots.children].forEach((b, k) => b.setAttribute('aria-current', String(k === i)));
      d.querySelectorAll('[data-peek-step]').forEach(b => {
        b.hidden = n < 2;
        b.disabled = Number(b.dataset.peekStep) < 0 ? i <= 0 : i >= n - 1;
      });
    }

    picked() {
      return [...this.querySelectorAll('[data-bundle-check]:checked')].filter(c => !c.closest('[data-bundle-item]').hidden);
    }

    update() {
      const extras = this.picked();
      const every = this.mainChecks();
      this.querySelectorAll('[data-bundle-item]').forEach(item => item.classList.toggle('is-on', !!item.querySelector('[data-bundle-check]:checked')));
      const eff = c => this.price({ price: Number(c.dataset.price || 0), kit: Number(c.dataset.kit || 0) });
      const count = extras.length + 1;
      const total = extras.reduce((sum, c) => sum + eff(c), this.main.price);
      const full = extras.reduce((sum, c) => sum + Number(c.dataset.price || 0), this.main.price);
      const savings = full - total;
      const set = (sel, text) => { const el = this.querySelector(sel); if (el) el.textContent = text; };
      set('[data-bundle-count]', count);
      set('[data-bundle-noun]', count === 1 ? 'item' : 'items');
      set('[data-bundle-total]', igMoney(total));
      const was = this.querySelector('[data-bundle-total-was]');
      if (was) {
        was.textContent = igMoney(full);
        was.hidden = savings <= 0;
      }
      let label = 'Add to cart';
      if (this.main.missing) label = 'Unavailable';
      else if (!this.main.available) label = 'Sold out';
      else if (count > 1) label = `Add ${count} items${savings > 0 ? ` · save ${igMoney(savings)}` : ' to cart'}`;
      set('[data-bundle-label]', label);
      if (this.button) this.button.disabled = !this.main.available;
      this.classList.toggle('has-extras', count > 1);
      this.classList.toggle('has-savings', savings > 0);

      // "Add the full kit": shown while something is still unticked, with what the whole kit saves
      if (this.allButton) {
        const left = every.filter(c => !c.checked);
        const fullSave = every.reduce((sum, c) => sum + Number(c.dataset.price || 0) - eff(c), 0);
        this.allButton.hidden = every.length < 2 || left.length === 0 || !this.main.available;
        const base = this.allButton.dataset.label || (this.allButton.dataset.label = this.querySelector('[data-bundle-all-label]').textContent);
        set('[data-bundle-all-label]', fullSave > 0 ? `${base} · save ${igMoney(fullSave)}` : base);
      }

      // receipt: one line per piece of the kit, then the savings
      const lines = this.querySelector('[data-bundle-lines]');
      if (lines) {
        const rows = [[this.querySelector('.ig-bundle__item--main .ig-bundle__name'), this.main.price]]
          .concat(extras.map(c => [c.closest('[data-bundle-item]').querySelector('.ig-bundle__name'), eff(c)]));
        const key = rows.map(r => (r[0] ? r[0].textContent : '') + r[1]).join('|') + savings;
        if (key !== this.linesKey) {
          this.linesKey = key;
          const make = (text, cents, cls) => {
            const li = document.createElement('li');
            if (cls) li.className = cls;
            const name = document.createElement('span');
            const price = document.createElement('span');
            name.textContent = text;
            price.textContent = cents;
            li.append(name, price);
            return li;
          };
          const items = rows.map(([nameEl, cents]) => make(nameEl ? nameEl.textContent.trim() : '', igMoney(cents)));
          if (savings > 0) items.push(make(this.dataset.savingsLabel || 'Kit savings', `−${igMoney(savings)}`, 'is-saving'));
          lines.replaceChildren(...items);
        }
      }
    }

    async add() {
      if (this.busy || !this.main.available) return;
      const error = this.querySelector('[data-bundle-error]');
      if (error) error.hidden = true;
      // the cart lists the newest line first: send the extras (reversed) before the saber so the saber leads the cart
      const items = [...this.picked().map(c => ({ id: Number(c.value), quantity: 1 })).reverse(), { id: Number(this.main.id), quantity: 1 }];
      const sections = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      this.busy = true;
      this.button.setAttribute('aria-busy', 'true');
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      try {
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: sections.join(','), sections_url: `${root}variants/${this.main.id}` })
        });
        let json = {};
        try { json = await res.json(); } catch (e) { json = {}; }
        if (!res.ok) throw new Error(json.description || json.message || 'We couldn’t add the kit to your cart. Please try again.');
        const settings = (window.themeVariables && window.themeVariables.settings) || {};
        if (settings.cartType === 'page' || settings.pageType === 'cart') {
          window.location.href = `${root}cart`;
          return;
        }
        const cart = await (await fetch(`${root}cart.js`)).json();
        cart.sections = json.sections;
        this.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: json.items || [json], cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
        this.classList.add('is-added');
        setTimeout(() => this.classList.remove('is-added'), 1600);
      } catch (e) {
        if (error) {
          error.textContent = (e && e.message && !/^(Failed to fetch|NetworkError|Load failed)/i.test(e.message)) ? e.message : 'Connection problem. Please try again.';
          error.hidden = false;
        }
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        this.busy = false;
        this.button.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('ig-bundle')) customElements.define('ig-bundle', IgBundle);

  /* ---- Add-on-only items ship with a saber (round 7) ------------------------------------------------------------
     Products of type "Add-On" (or tagged "Add-On Only") are sold through the kit builder and ship with a saber. If the
     saber leaves the cart, they come out too, with a short note. Which lines are sabers is decided on the server
     (sections/ig-cart-guard.liquid: products in the Saber collection from Theme settings > Saber kits).
     Checkout itself can't be blocked from the theme; this keeps the cart honest. */
  const igToast = text => {
    let t = document.querySelector('.ig-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'ig-toast';
      t.setAttribute('role', 'status');
      t.setAttribute('aria-live', 'polite');
      document.body.appendChild(t);
    }
    t.textContent = text;
    requestAnimationFrame(() => t.classList.add('is-on'));
    clearTimeout(t.igTimer);
    t.igTimer = setTimeout(() => t.classList.remove('is-on'), 7000);
  };

  const IgAddonGuard = {
    root: () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/',
    // the server decides (type "Add-On" or tag "Add-On Only"; a saber = in the saber collection), so any non-empty
    // cart is checked once per change; a change that lands while a check runs is checked right after it
    run() {
      if (this.busy) {
        this.again = true;
        return this.busy;
      }
      this.busy = this.check().catch(() => {}).finally(() => {
        this.busy = null;
        if (this.again) {
          this.again = false;
          this.run();
        }
      });
      return this.busy;
    },
    remember(count) {
      try { sessionStorage.setItem('igGuardCount', String(count)); } catch (e) { /* private mode */ }
    },
    async check() {
      const root = this.root();
      const html = await (await fetch(`${root}?section_id=ig-cart-guard&t=${Date.now()}`)).text();
      const box = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-guard]');
      if (!box) return;
      const data = JSON.parse(box.textContent);
      if (!data || data.sabers > 0 || !Array.isArray(data.addons) || !data.addons.length) {
        this.remember(data && typeof data.count === 'number' ? data.count : '');
        return;
      }
      const updates = {};
      data.addons.forEach(a => { updates[a.key] = 0; });
      const res = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!res.ok) return;
      let after = null;
      try { after = await res.json(); } catch (e) { after = null; }
      if (after && typeof after.item_count === 'number') this.remember(after.item_count);
      const names = [...new Set(data.addons.map(a => a.title))];
      const list = names.length > 2 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names.join(' and ');
      const msg = `${list} ${names.length === 1 ? 'was' : 'were'} removed. Add-ons ship with a saber: add one to your cart and pick them in its kit builder.`;
      const settings = (window.themeVariables && window.themeVariables.settings) || {};
      if (settings.pageType === 'cart') {
        try { sessionStorage.setItem('igAddonNote', msg); } catch (e) { /* private mode */ }
        window.location.reload();
        return;
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      igToast(msg);
    }
  };

  document.addEventListener('cart:change', e => {
    const cart = e.detail && e.detail.cart;
    if (cart && cart.item_count > 0) IgAddonGuard.run();
    else if (cart) IgAddonGuard.remember(0);
  });

  // Background videos (video band): load and play only near the screen, pause when scrolled away
  const igLazyVideos = () => {
    const vids = [...document.querySelectorAll('.ig-cinema > video, video[data-ig-lazy-video]')];
    if (!vids.length) return;
    const still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (still || !('IntersectionObserver' in window)) {
      if (!still) vids.forEach(v => { v.muted = true; v.play().catch(() => {}); });
      return;
    }
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        const v = e.target;
        if (e.isIntersecting) {
          v.muted = true;
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { rootMargin: '300px 0px' });
    vids.forEach(v => io.observe(v));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', igLazyVideos);
  else igLazyVideos();

  // Sticky add-to-cart bar: steps aside while the kit builder (which has its own add button) is on screen
  const igQuickAddMute = () => {
    const bar = document.querySelector('product-quick-add');
    const kit = document.querySelector('ig-bundle');
    if (!bar || !kit || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(entries => {
      entries.forEach(e => bar.classList.toggle('ig-muted', e.isIntersecting));
    }).observe(kit);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', igQuickAddMute);
  else igQuickAddMute();

  // Navigation blade: a thin light runs across the top of the screen while the next page loads
  const igNavBlade = () => {
    const bar = document.createElement('div');
    bar.className = 'ig-navblade';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    let timer = 0;
    const stop = () => {
      clearTimeout(timer);
      bar.classList.remove('is-on');
    };
    document.addEventListener('click', e => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download') || a.hasAttribute('aria-controls')) return;
      let url;
      try { url = new URL(a.href, location.href); } catch (err) { return; }
      if (url.origin !== location.origin || (url.pathname === location.pathname && url.search === location.search)) return;
      bar.classList.remove('is-on');
      void bar.offsetWidth;
      bar.classList.add('is-on');
      clearTimeout(timer);
      timer = setTimeout(stop, 8000);
    });
    window.addEventListener('pageshow', stop);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', igNavBlade);
  else igNavBlade();

  // Card edge light: on mouse devices the card border lights up around the pointer (CSS reads --mx / --my)
  if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    let lastMove = null;
    let moveFrame = 0;
    document.addEventListener('pointermove', e => {
      lastMove = e;
      if (moveFrame) return;
      moveFrame = requestAnimationFrame(() => {
        moveFrame = 0;
        const t = lastMove.target;
        const card = t && t.closest ? t.closest('.ig-card, .ig-review') : null;
        if (!card) return;
        const r = card.getBoundingClientRect();
        card.style.setProperty('--mx', `${Math.round(lastMove.clientX - r.left)}px`);
        card.style.setProperty('--my', `${Math.round(lastMove.clientY - r.top)}px`);
      });
    }, { passive: true });
  }

  const igGuardOnLoad = () => {
    try {
      const note = sessionStorage.getItem('igAddonNote');
      if (note) {
        sessionStorage.removeItem('igAddonNote');
        igToast(note);
      }
    } catch (e) { /* private mode */ }
    // on page load, only when the cart changed since the last check (e.g. the cart page after a quantity change)
    const el = document.querySelector('cart-count');
    const count = el ? Number(el.textContent.trim()) : 0;
    let seen = null;
    try { seen = sessionStorage.getItem('igGuardCount'); } catch (e) { seen = null; }
    if (count > 0 && seen !== String(count)) IgAddonGuard.run();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', igGuardOnLoad);
  else igGuardOnLoad();

  // Compact "See it in action" card: the whole card starts the video, which then expands in place
  document.addEventListener('click', e => {
    const trigger = e.target.closest && e.target.closest('[data-ig-play]');
    if (!trigger) return;
    const card = trigger.closest('.ig-action__player');
    const facade = card && card.querySelector('.ig-video');
    if (facade) playVideo(facade);
  });

  // Links to #compare-builds / #compare-cores (core guide, footnotes): open the Core lab on the right tab, or go to the product page
  const openCompare = () => {
    const panel = document.getElementById('compare-builds');
    const lab = (panel && panel.closest('ig-cores')) || document.getElementById('compare-cores');
    if (!lab) return false;
    const tab = lab.querySelector('[data-view="table"]');
    if (panel && tab && lab.view) lab.view('table');
    lab.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    if (tab && panel) tab.focus({ preventScroll: true });
    return true;
  };
  document.addEventListener('click', e => {
    const link = e.target.closest && e.target.closest('a[href="#compare-builds"]');
    if (link && openCompare()) e.preventDefault();
  });
  const tidyCompareLinks = () => {
    // a product template without the Core lab: the in-page link has nowhere to go
    if (document.getElementById('compare-builds') || document.getElementById('compare-cores')) return;
    document.querySelectorAll('.ally-core-guide__footnote a[href="#compare-builds"]').forEach(a => { a.closest('.ally-core-guide__footnote').hidden = true; });
  };
  tidyCompareLinks();
  document.addEventListener('product:rerender', () => setTimeout(tidyCompareLinks, 0));
  if (location.hash === '#compare-builds') window.addEventListener('load', () => setTimeout(openCompare, 300));

  /* Theme editor support */
  document.addEventListener('shopify:section:load', e => observeReveals(e.target));
  document.addEventListener('shopify:block:select', e => {
    const hero = e.target.closest('ig-hero');
    if (hero && hero.go) {
      const i = [...hero.querySelectorAll('.ig-hero__slide')].indexOf(e.target);
      if (i > -1) { hero.pause(); hero.go(i, true); }
    }

  });
})();
